import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { BADGE_COLORS } from "@/components/HighlightBadge";
import { DatePicker } from "@/components/DatePicker";

// Helper visual: muestra paleta de colores de viñeta + vista previa con texto/color
function BadgeColorPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <span className="text-[10px] text-gray-500 mr-1">Color:</span>
      {BADGE_COLORS.map(c => {
        const active = (value || "") === c.value;
        return (
          <button
            type="button"
            key={c.label}
            onClick={() => onChange(c.value)}
            title={c.label}
            className={`w-6 h-6 rounded-full border-2 transition-all ${active ? "border-white ring-2 ring-white/30" : "border-white/20 hover:border-white/50"}`}
            style={{ backgroundColor: c.hex }}
            data-testid={`badge-color-${c.label.toLowerCase()}`}
          />
        );
      })}
    </div>
  );
}

function badgePreviewStyle(color) {
  const bg = color || "#FF4D00";
  // YIQ luminancia perceptual para decidir texto blanco o negro
  const h = bg.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 165;
  return { backgroundColor: bg, color: isLight ? "#0A0A0A" : "#FFFFFF" };
}
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Mountains, Plus, PencilSimple, Trash, SignOut, Calendar, Barbell, ShoppingBag, Package, AddressBook, ClipboardText, DownloadSimple, Images, Upload, X, Bell, BellSlash, Info, House, Copy, FileXls, ChatCircle, WhatsappLogo, MagnifyingGlass, ShieldCheck, PaperPlaneTilt, Lock } from "@phosphor-icons/react";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { compressImage, formatBytes } from "@/utils/imageCompress";
import { getFocusMeta } from "@/utils/focusMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
}

async function apiGet(path) {
  const { data } = await axios.get(`${API}${path}`, { headers: getHeaders() });
  return data;
}
async function apiPost(path, body) {
  const { data } = await axios.post(`${API}${path}`, body, { headers: getHeaders() });
  return data;
}
async function apiPut(path, body) {
  const { data } = await axios.put(`${API}${path}`, body, { headers: getHeaders() });
  return data;
}
async function apiDelete(path) {
  const { data } = await axios.delete(`${API}${path}`, { headers: getHeaders() });
  return data;
}
async function apiPatch(path, body) {
  const { data } = await axios.patch(`${API}${path}`, body, { headers: getHeaders() });
  return data;
}
async function downloadAuthenticated(path, filename, mime) {
  try {
    const res = await axios.get(`${API}${path}`, { headers: getHeaders(), responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Descarga lista");
  } catch (e) {
    toast.error(e?.response?.status === 401 ? "Sesión expirada. Vuelve a iniciar sesión." : "Error al descargar");
  }
}

// ── Events Tab ──
function EventsTab() {
  const [items, setItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", date: "", start_time: "", show_description: true, location: "", distance_km: "", elevation_m: "", difficulty: "intermedio", image_url: "", price: "", max_participants: "50", distances: [], drive_link: "", location_url: "", suggested_products: [], links: [], start_lat: "", start_lng: "", photos: [], visible: true, registration_fields: [], badge_text: "", badge_color: "", consent_text: "", consent_clauses: [], registration_attachments: [], categories: [], reminder_extra_text: "", requires_access_code: false, privacy_mode: "gated", access_codes: [] });

  const load = useCallback(async () => { try { setItems(await apiGet("/events?include_hidden=true")); } catch {} }, []);
  const loadProducts = useCallback(async () => { try { setAllProducts(await apiGet("/products")); } catch {} }, []);
  useEffect(() => { load(); loadProducts(); }, [load, loadProducts]);

  const resetForm = () => setForm({ title: "", description: "", date: "", start_time: "", show_description: true, location: "", distance_km: "", elevation_m: "", difficulty: "intermedio", image_url: "", price: "", max_participants: "50", distances: [], drive_link: "", location_url: "", suggested_products: [], links: [], start_lat: "", start_lng: "", photos: [], visible: true, notifications_enabled: true, registration_fields: [], badge_text: "", badge_color: "", consent_text: "", consent_clauses: [], registration_attachments: [], categories: [], reminder_extra_text: "", requires_access_code: false, privacy_mode: "gated", access_codes: [] });

  const openCreate = () => { resetForm(); setEditing(null); setOpen(true); };
  const openEdit = (item) => {
    setForm({ title: item.title, description: item.description, date: item.date, start_time: item.start_time || "", show_description: item.show_description !== false, location: item.location, distance_km: String(item.distance_km || ""), elevation_m: String(item.elevation_m || ""), difficulty: item.difficulty, image_url: item.image_url || "", price: String(item.price || ""), max_participants: String(item.max_participants || "50"), distances: item.distances || [], drive_link: item.drive_link || "", location_url: item.location_url || "", suggested_products: item.suggested_products || [], links: item.links || [], start_lat: item.start_lat ? String(item.start_lat) : "", start_lng: item.start_lng ? String(item.start_lng) : "", photos: item.photos || [], visible: item.visible !== false, notifications_enabled: item.notifications_enabled !== false, registration_fields: item.registration_fields || [], badge_text: item.badge_text || "", badge_color: item.badge_color || "", consent_text: item.consent_text || "", consent_clauses: item.consent_clauses || [], registration_attachments: item.registration_attachments || [], categories: item.categories || [], reminder_extra_text: item.reminder_extra_text || "", requires_access_code: item.requires_access_code === true, privacy_mode: item.privacy_mode || "gated", access_codes: item.access_codes || [] });
    setEditing(item.id);
    setOpen(true);
  };

  const addDistance = () => setForm(p => ({ ...p, distances: [...p.distances, { label: "", km: "", description: "", price: "", map_url: "", max_participants: 0 }] }));
  const removeDistance = (idx) => setForm(p => ({ ...p, distances: p.distances.filter((_, i) => i !== idx) }));
  const updateDistance = (idx, field, value) => setForm(p => ({ ...p, distances: p.distances.map((d, i) => i === idx ? { ...d, [field]: value } : d) }));

  const handleSave = async () => {
    const distances = form.distances.map(d => ({ label: d.label, km: Number(d.km) || 0, description: d.description, price: Number(d.price) || 0, map_url: d.map_url || "", elevation_gain: Number(d.elevation_gain) || 0, elevation_image_url: d.elevation_image_url || "", track_data: d.track_data || [], elevation_profile: d.elevation_profile || [], markers: d.markers || [], max_participants: Number(d.max_participants) || 0, photos: d.photos || [] })).filter(d => d.label);
    const body = { ...form, distance_km: form.distance_km ? Number(form.distance_km) : null, elevation_m: form.elevation_m ? Number(form.elevation_m) : null, price: Number(form.price) || 0, max_participants: Number(form.max_participants) || 50, distances, drive_link: form.drive_link, location_url: form.location_url, suggested_products: form.suggested_products, links: form.links || [], start_lat: form.start_lat ? Number(form.start_lat) : null, start_lng: form.start_lng ? Number(form.start_lng) : null, photos: form.photos || [], visible: form.visible !== false, notifications_enabled: form.notifications_enabled !== false, registration_fields: form.registration_fields || [], consent_text: form.consent_text || "", consent_clauses: (form.consent_clauses || []).filter(c => (c.title || "").trim()), registration_attachments: (form.registration_attachments || []).filter(a => (a.label || "").trim() && (a.key || "").trim()), categories: (form.categories || []).filter(c => (c.name || "").trim()).map(c => ({ name: (c.name || "").trim(), min_age: Number(c.min_age) || 0, max_age: Number(c.max_age) || 0 })), reminder_extra_text: form.reminder_extra_text || "", requires_access_code: form.requires_access_code === true, privacy_mode: form.privacy_mode === "hidden" ? "hidden" : "gated", access_codes: (form.access_codes || []).filter(c => (c.code || "").trim()).slice(0, 10).map(c => ({ code: (c.code || "").trim().toUpperCase(), label: (c.label || "").trim(), max_uses: Number(c.max_uses) || 0, expires_at: (c.expires_at || "").trim(), active: c.active !== false })) };
    try {
      if (editing) { await apiPut(`/events/${editing}`, body); toast.success("Evento actualizado"); }
      else { await apiPost("/events", body); toast.success("Evento creado"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este evento?")) return;
    try { await apiDelete(`/events/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Eventos ({items.length})</h3>
        <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500]" onClick={openCreate} data-testid="admin-add-event"><Plus size={14} className="mr-1" />Nuevo</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Titulo</TableHead><TableHead>Fecha</TableHead><TableHead>Distancias</TableHead><TableHead>Dificultad</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(ev => (
            <TableRow key={ev.id}>
              <TableCell className="font-medium">{ev.title}{ev.visible === false && <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 text-[10px]" data-testid={`event-hidden-badge-${ev.id}`}>Oculto</Badge>}</TableCell>
              <TableCell className="text-gray-400">{ev.date}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {(ev.distances || []).map((d, i) => (
                    <Badge key={i} variant="outline" className="text-xs text-[#FF4D00] border-[#FF4D00]/30">{d.label}</Badge>
                  ))}
                  {(!ev.distances || ev.distances.length === 0) && <span className="text-xs text-gray-500">-</span>}
                </div>
              </TableCell>
              <TableCell><Badge variant="outline" className="text-xs">{ev.difficulty}</Badge></TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className={ev.notifications_enabled === false ? "text-gray-500 hover:text-gray-300" : "text-[#FF4D00] hover:text-[#FF4D00]/80"}
                    onClick={async () => {
                      try {
                        const { data } = await axios.patch(`${API}/events/${ev.id}/notifications-toggle`, {}, { headers: getHeaders() });
                        toast.success(data.notifications_enabled ? "Notificaciones activadas" : "Notificaciones silenciadas");
                        load();
                      } catch { toast.error("Error"); }
                    }}
                    title={ev.notifications_enabled === false ? "Notificaciones silenciadas - click para activar" : "Notificaciones activas - click para silenciar"}
                    data-testid={`admin-toggle-notifs-event-${ev.id}`}
                  >
                    {ev.notifications_enabled === false ? <BellSlash size={14} /> : <Bell size={14} />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(ev)} data-testid={`admin-edit-event-${ev.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(ev.id)} data-testid={`admin-delete-event-${ev.id}`}><Trash size={14} /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Evento" : "Nuevo Evento"}</DialogTitle><DialogDescription>Completa los campos del evento</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-400">Titulo</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="event-form-title" /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-400">Descripcion General</Label>
                <label className="flex items-center gap-1.5 text-[11px] text-gray-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.show_description !== false}
                    onChange={e => setForm(p => ({ ...p, show_description: e.target.checked }))}
                    className="accent-[#FF4D00]"
                    data-testid="event-form-show-description"
                  />
                  Mostrar en pagina del evento
                </label>
              </div>
              <Textarea className="bg-[#1A1A1A] border-white/10 mt-1" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="event-form-description" />
              {!form.show_description && <p className="text-[10px] text-yellow-500/80 mt-1">⚠ La descripcion no se mostrara al publico (queda guardada pero oculta).</p>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-400">Fecha</Label>
                <div className="mt-1"><DatePicker value={form.date || ""} onChange={(v) => setForm(p => ({ ...p, date: v }))} testId="event-form-date" /></div>
              </div>
              <div><Label className="text-xs text-gray-400">Hora inicio (opcional)</Label><Input type="time" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} data-testid="event-form-start-time" /></div>
              <div className="col-span-2 sm:col-span-1"><Label className="text-xs text-gray-400">Ubicacion</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} data-testid="event-form-location" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-gray-400">Desnivel general (m)</Label><Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.elevation_m} onChange={e => setForm(p => ({ ...p, elevation_m: e.target.value }))} /></div>
              <div>
                <Label className="text-xs text-gray-400">
                  Capacidad total
                  <span className="text-[10px] text-gray-500 ml-1">(suma de cupos por distancia)</span>
                </Label>
                <Input
                  type="number"
                  className="bg-[#1A1A1A] border-white/10 mt-1 text-gray-300"
                  value={(form.distances || []).reduce((sum, d) => sum + (Number(d.max_participants) || 0), 0) || form.max_participants}
                  onChange={e => setForm(p => ({ ...p, max_participants: e.target.value }))}
                  readOnly={(form.distances || []).some(d => Number(d.max_participants) > 0)}
                  data-testid="event-form-total-capacity"
                />
                <p className="text-[10px] text-gray-500 mt-1">Se calcula sumando los cupos de cada distancia. Define los cupos abajo en "Distancias del Evento".</p>
              </div>
              <div><Label className="text-xs text-gray-400">Dificultad</Label>
                <Select value={form.difficulty} onValueChange={v => setForm(p => ({ ...p, difficulty: v }))}>
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="principiante">Principiante</SelectItem><SelectItem value="intermedio">Intermedio</SelectItem><SelectItem value="avanzado">Avanzado</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs text-gray-400">URL Imagen</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} /></div>

            {/* Viñeta destacable */}
            <div className="p-3 border border-white/10 bg-[#0F0F0F]" data-testid="event-form-badge-block">
              <Label className="text-xs text-gray-400">Viñeta destacada (opcional)</Label>
              <Input
                className="bg-[#1A1A1A] border-white/10 mt-1 uppercase"
                value={form.badge_text || ""}
                maxLength={12}
                onChange={e => setForm(p => ({ ...p, badge_text: e.target.value }))}
                placeholder="OFERTA · 30% · NUEVO · LIMITADO"
                data-testid="event-form-badge-text"
              />
              <p className="text-[10px] text-gray-500 mt-1">Texto corto (max 12 caracteres) que aparece en la esquina de la imagen. Deja vacio para no mostrar.</p>
              <BadgeColorPicker value={form.badge_color} onChange={c => setForm(p => ({ ...p, badge_color: c }))} />
              {form.badge_text && (
                <div className="mt-2 inline-block relative bg-[#1A1A1A] border border-white/10 p-2">
                  <span className="text-[10px] text-gray-500 mr-2">Vista previa:</span>
                  <span className="inline-block text-[10px] font-black uppercase tracking-wider px-2.5 py-1 shadow-lg" style={badgePreviewStyle(form.badge_color)}>{form.badge_text}</span>
                </div>
              )}
            </div>

            {/* Visibilidad */}
            <div className={`flex items-center justify-between p-3 border ${form.visible ? "bg-emerald-500/5 border-emerald-500/30" : "bg-yellow-500/5 border-yellow-500/30"}`} data-testid="event-form-visibility">
              <div>
                <p className="text-sm font-semibold">{form.visible ? "✓ Evento publico" : "⚠ Solo visible para admin (borrador)"}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{form.visible ? "Visible en la seccion Eventos para todos los visitantes" : "Oculto del sitio publico. Solo tu lo ves en el panel. Util para preparar eventos antes de publicarlos."}</p>
              </div>
              <label className="relative inline-block w-11 h-6 cursor-pointer flex-shrink-0">
                <input type="checkbox" className="sr-only peer" checked={form.visible} onChange={e => setForm(p => ({ ...p, visible: e.target.checked }))} data-testid="event-form-visible-toggle" />
                <span className="absolute inset-0 bg-gray-700 peer-checked:bg-emerald-500 rounded-full transition-colors"></span>
                <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></span>
              </label>
            </div>

            {/* Notificaciones in-app (campanita admin) */}
            <div className={`flex items-center justify-between p-3 border ${form.notifications_enabled ? "bg-[#FF4D00]/5 border-[#FF4D00]/30" : "bg-gray-500/5 border-gray-500/30"}`} data-testid="event-form-notifications">
              <div className="flex items-start gap-2">
                {form.notifications_enabled ? <Bell size={18} className="text-[#FF4D00] mt-0.5" /> : <BellSlash size={18} className="text-gray-400 mt-0.5" />}
                <div>
                  <p className="text-sm font-semibold">{form.notifications_enabled ? "Notificaciones activadas" : "Notificaciones silenciadas"}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{form.notifications_enabled ? "Cada inscripcion / pago genera una notificacion en la campanita del admin." : "No se generaran notificaciones en la campanita para este evento. Util en eventos masivos para evitar ruido."}</p>
                </div>
              </div>
              <label className="relative inline-block w-11 h-6 cursor-pointer flex-shrink-0">
                <input type="checkbox" className="sr-only peer" checked={form.notifications_enabled} onChange={e => setForm(p => ({ ...p, notifications_enabled: e.target.checked }))} data-testid="event-form-notifications-toggle" />
                <span className="absolute inset-0 bg-gray-700 peer-checked:bg-[#FF4D00] rounded-full transition-colors"></span>
                <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></span>
              </label>
            </div>

            {/* Campos de inscripcion (checklist) */}
            <div className="p-3 border border-white/10 bg-[#0F0F0F]" data-testid="event-form-registration-fields">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-400 mb-1">Datos pedidos en la inscripcion</p>
              <p className="text-[11px] text-gray-500 mb-3">Nombre, email, telefono, sexo y categoria son siempre obligatorios. Marca los datos adicionales que requieres para este evento:</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {[
                  {key:"club", label:"Club"},
                  {key:"region", label:"Region"},
                  {key:"pais", label:"Pais"},
                  {key:"ciudad", label:"Ciudad"},
                  {key:"edad", label:"Edad"},
                  {key:"fecha_nacimiento", label:"Fecha de nacimiento"},
                  {key:"rut", label:"RUT/DNI"},
                  {key:"talla_remera", label:"Talla de polera"},
                  {key:"emergencia_nombre", label:"Contacto emergencia (nombre)"},
                  {key:"emergencia_telefono", label:"Contacto emergencia (tel)"},
                  {key:"grupo_sanguineo", label:"Grupo sanguineo"},
                  {key:"alergias", label:"Alergias / medicas"},
                  {key:"objetivo", label:"Objetivo en el evento"},
                ].map(opt => {
                  const checked = (form.registration_fields || []).includes(opt.key);
                  return (
                    <label key={opt.key} className="flex items-center gap-2 cursor-pointer hover:text-white text-gray-300">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setForm(p => ({
                          ...p,
                          registration_fields: e.target.checked
                            ? [...(p.registration_fields || []), opt.key]
                            : (p.registration_fields || []).filter(x => x !== opt.key),
                        }))}
                        className="accent-[#FF4D00]"
                        data-testid={`event-regfield-${opt.key}`}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Consentimiento informado */}
            <div className="pt-3 border-t border-white/10" data-testid="event-form-consent">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={16} className="text-[#FF4D00]" />
                <label className="text-sm font-semibold">Consentimiento informado (opcional)</label>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                Configura las cláusulas que el participante debe aceptar (cada una es un checkbox independiente). Además firmará dibujando y escribiendo su nombre, RUT y contacto de emergencia. Menores requieren apoderado. Se envía copia PDF por email.
              </p>

              {/* Preámbulo opcional */}
              <div className="mb-3">
                <label className="text-[10px] uppercase tracking-widest text-gray-500">Preámbulo (opcional)</label>
                <textarea
                  value={form.consent_text}
                  onChange={e => setForm(p => ({ ...p, consent_text: e.target.value }))}
                  rows={3}
                  placeholder="Texto introductorio que se muestra sobre las cláusulas."
                  className="w-full bg-[#0F0F0F] border border-white/10 p-2 text-xs text-white focus:border-[#FF4D00] outline-none whitespace-pre-wrap mt-1"
                  data-testid="event-form-consent-text"
                />
              </div>

              {/* Cláusulas */}
              <div className="space-y-3" data-testid="event-form-consent-clauses">
                {(form.consent_clauses || []).map((cl, idx) => (
                  <div key={idx} className="bg-[#0F0F0F] border border-white/10 p-3" data-testid={`consent-clause-${idx}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-[#FF4D00] w-6">#{idx + 1}</span>
                      <input
                        value={cl.title || ""}
                        onChange={e => setForm(p => ({
                          ...p,
                          consent_clauses: p.consent_clauses.map((c, i) => i === idx ? { ...c, title: e.target.value } : c),
                        }))}
                        placeholder="Título de la cláusula (ej: Desligue de responsabilidad)"
                        className="flex-1 bg-[#1A1A1A] border border-white/10 h-9 px-2 text-sm text-white focus:border-[#FF4D00] outline-none"
                        data-testid={`consent-clause-title-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, consent_clauses: p.consent_clauses.filter((_, i) => i !== idx) }))}
                        className="text-red-400 hover:text-red-300 p-1"
                        data-testid={`consent-clause-remove-${idx}`}
                        title="Eliminar cláusula"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                    <textarea
                      value={cl.text || ""}
                      onChange={e => setForm(p => ({
                        ...p,
                        consent_clauses: p.consent_clauses.map((c, i) => i === idx ? { ...c, text: e.target.value } : c),
                      }))}
                      rows={3}
                      placeholder="Texto de la cláusula. El participante debe aceptarla con un checkbox individual."
                      className="w-full bg-[#1A1A1A] border border-white/10 p-2 text-xs text-white focus:border-[#FF4D00] outline-none whitespace-pre-wrap"
                      data-testid={`consent-clause-text-${idx}`}
                    />
                    <div className="flex gap-1 mt-2 justify-end">
                      <button
                        type="button"
                        onClick={() => setForm(p => {
                          const arr = [...p.consent_clauses];
                          if (idx > 0) [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                          return { ...p, consent_clauses: arr };
                        })}
                        disabled={idx === 0}
                        className="text-[11px] text-gray-400 disabled:text-gray-700 hover:text-white px-2"
                        data-testid={`consent-clause-up-${idx}`}
                        title="Subir"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm(p => {
                          const arr = [...p.consent_clauses];
                          if (idx < arr.length - 1) [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                          return { ...p, consent_clauses: arr };
                        })}
                        disabled={idx === (form.consent_clauses || []).length - 1}
                        className="text-[11px] text-gray-400 disabled:text-gray-700 hover:text-white px-2"
                        data-testid={`consent-clause-down-${idx}`}
                        title="Bajar"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-3 h-8 text-xs border-[#FF4D00]/40 text-[#FF4D00]"
                onClick={() => setForm(p => ({ ...p, consent_clauses: [...(p.consent_clauses || []), { title: "", text: "" }] }))}
                data-testid="consent-clause-add"
              >
                <Plus size={12} className="mr-1" /> Agregar cláusula
              </Button>

              {((form.consent_clauses || []).some(c => (c.title || "").trim()) || (form.consent_text || "").trim()) && (
                <p className="text-[11px] text-green-400 mt-3">✓ La firma será obligatoria al inscribirse. Los inscritos existentes recibirán un link por email.</p>
              )}
            </div>

            {/* Categorías por edad (opcional). Si vacío, se usan las 5 por defecto en el formulario de inscripción. */}
            <div className="pt-3 border-t border-white/10" data-testid="event-form-categories">
              <div className="flex items-center gap-2 mb-2">
                <PencilSimple size={16} className="text-[#FF4D00]" />
                <label className="text-sm font-semibold">Categorías por edad (opcional)</label>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                Define categorías personalizadas con su rango de edad. Ej: Juvenil 15-17, Adulto 18-39, Master 40-49, Senior 50+. Deja min o max en 0 = sin límite. Si vacío, se usan las 5 categorías por defecto (General, Sub 23, Master, Senior Master, Gran Master).
              </p>
              <div className="space-y-2">
                {(form.categories || []).map((cat, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-[#0F0F0F] border border-white/10 p-2" data-testid={`category-row-${idx}`}>
                    <span className="text-[10px] font-bold text-[#FF4D00] w-6">#{idx + 1}</span>
                    <input
                      value={cat.name || ""}
                      onChange={e => setForm(p => ({ ...p, categories: p.categories.map((c, i) => i === idx ? { ...c, name: e.target.value } : c) }))}
                      placeholder="Nombre (ej: Master)"
                      className="flex-1 bg-[#1A1A1A] border border-white/10 h-8 px-2 text-sm text-white focus:border-[#FF4D00] outline-none"
                      data-testid={`category-name-${idx}`}
                    />
                    <input
                      type="number"
                      min="0"
                      value={cat.min_age ?? ""}
                      onChange={e => setForm(p => ({ ...p, categories: p.categories.map((c, i) => i === idx ? { ...c, min_age: e.target.value } : c) }))}
                      placeholder="Min"
                      className="w-16 bg-[#1A1A1A] border border-white/10 h-8 px-2 text-sm text-white focus:border-[#FF4D00] outline-none"
                      data-testid={`category-min-${idx}`}
                    />
                    <span className="text-gray-500 text-xs">—</span>
                    <input
                      type="number"
                      min="0"
                      value={cat.max_age ?? ""}
                      onChange={e => setForm(p => ({ ...p, categories: p.categories.map((c, i) => i === idx ? { ...c, max_age: e.target.value } : c) }))}
                      placeholder="Max"
                      className="w-16 bg-[#1A1A1A] border border-white/10 h-8 px-2 text-sm text-white focus:border-[#FF4D00] outline-none"
                      data-testid={`category-max-${idx}`}
                    />
                    <span className="text-[10px] text-gray-500 w-14 text-right">
                      {Number(cat.min_age) > 0 || Number(cat.max_age) > 0
                        ? `${Number(cat.min_age) || "–"}–${Number(cat.max_age) || "∞"}`
                        : "sin lím."}
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, categories: p.categories.filter((_, i) => i !== idx) }))}
                      className="text-red-400 hover:text-red-300 p-1"
                      data-testid={`category-remove-${idx}`}
                      title="Eliminar categoría"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 h-8 text-xs border-[#FF4D00]/40 text-[#FF4D00]"
                onClick={() => setForm(p => ({ ...p, categories: [...(p.categories || []), { name: "", min_age: 0, max_age: 0 }] }))}
                data-testid="category-add"
              >
                <Plus size={12} className="mr-1" /> Agregar categoría
              </Button>
            </div>

            {/* Recordatorio automático 48h antes */}
            <div className="pt-3 border-t border-white/10" data-testid="event-form-reminder">
              <div className="flex items-center gap-2 mb-2">
                <PaperPlaneTilt size={16} className="text-[#FF4D00]" />
                <label className="text-sm font-semibold">Recordatorio automático (48h antes)</label>
              </div>
              <p className="text-[11px] text-gray-500 mb-2">
                Se envía por email a todos los inscritos exactamente 48 horas antes del evento (todos los días a las 07:00 hora Chile). Incluye fecha, hora de partida y ubicación automáticamente. Aquí puedes agregar texto extra: checklist de gear obligatorio, instrucciones especiales, etc.
              </p>
              <textarea
                value={form.reminder_extra_text}
                onChange={e => setForm(p => ({ ...p, reminder_extra_text: e.target.value }))}
                rows={5}
                placeholder="Ej: Gear obligatorio: chaqueta impermeable, gorro, guantes, silbato...&#10;Llegar 60 min antes para retirar tu número."
                className="w-full bg-[#0F0F0F] border border-white/10 p-2 text-xs text-white focus:border-[#FF4D00] outline-none whitespace-pre-wrap"
                data-testid="event-form-reminder-text"
              />
            </div>

            {/* ── Privacidad y códigos de acceso ── */}
            <div className="pt-3 border-t border-white/10" data-testid="event-form-access-codes">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={16} className="text-[#FF4D00]" />
                <label className="text-sm font-semibold">Evento privado (códigos de acceso)</label>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                Restringe la inscripción a personas con código. Puedes generar hasta 10 códigos, cada uno con su cupo. Ideal para eventos por invitación o parcialmente privados.
              </p>
              <label className="flex items-center gap-2 text-xs text-gray-300 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.requires_access_code}
                  onChange={e => setForm(p => ({ ...p, requires_access_code: e.target.checked }))}
                  data-testid="event-form-requires-access-code"
                />
                <span>Este evento requiere código de acceso para inscribirse</span>
              </label>

              {form.requires_access_code && (
                <>
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Modo de privacidad</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className={`flex items-start gap-2 border p-3 cursor-pointer text-xs ${form.privacy_mode !== "hidden" ? "border-[#FF4D00]/60 bg-[#FF4D00]/10" : "border-white/10 bg-[#0F0F0F]"}`}>
                        <input
                          type="radio"
                          name="privacy_mode"
                          checked={form.privacy_mode !== "hidden"}
                          onChange={() => setForm(p => ({ ...p, privacy_mode: "gated" }))}
                          data-testid="privacy-mode-gated"
                        />
                        <div>
                          <p className="font-bold text-white">Parcialmente privado</p>
                          <p className="text-gray-400 mt-0.5">Visible en el listado público. El código sólo se pide al inscribirse.</p>
                        </div>
                      </label>
                      <label className={`flex items-start gap-2 border p-3 cursor-pointer text-xs ${form.privacy_mode === "hidden" ? "border-[#FF4D00]/60 bg-[#FF4D00]/10" : "border-white/10 bg-[#0F0F0F]"}`}>
                        <input
                          type="radio"
                          name="privacy_mode"
                          checked={form.privacy_mode === "hidden"}
                          onChange={() => setForm(p => ({ ...p, privacy_mode: "hidden" }))}
                          data-testid="privacy-mode-hidden"
                        />
                        <div>
                          <p className="font-bold text-white">Totalmente privado</p>
                          <p className="text-gray-400 mt-0.5">No aparece en el listado público. Sólo accesible con link + código.</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Códigos ({(form.access_codes || []).length}/10)</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if ((form.access_codes || []).length >= 10) { toast.error("Máximo 10 códigos"); return; }
                            const rand = "PV-" + Math.random().toString(36).slice(2, 8).toUpperCase();
                            setForm(p => ({ ...p, access_codes: [...(p.access_codes || []), { code: rand, label: "", max_uses: 10, expires_at: "", active: true }] }));
                          }}
                          className="text-[11px] uppercase tracking-wider text-[#FF4D00] hover:text-orange-400 border border-[#FF4D00]/30 px-2 py-1"
                          data-testid="access-code-generate"
                        >
                          + Generar aleatorio
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if ((form.access_codes || []).length >= 10) { toast.error("Máximo 10 códigos"); return; }
                            setForm(p => ({ ...p, access_codes: [...(p.access_codes || []), { code: "", label: "", max_uses: 10, expires_at: "", active: true }] }));
                          }}
                          className="text-[11px] uppercase tracking-wider text-gray-300 hover:text-white border border-white/20 px-2 py-1"
                          data-testid="access-code-add-manual"
                        >
                          + Manual
                        </button>
                      </div>
                    </div>
                    {(form.access_codes || []).length === 0 && (
                      <p className="text-[11px] text-gray-500 italic bg-[#0F0F0F] border border-white/5 p-3">
                        Aún no hay códigos. Agrega uno con &quot;+ Generar aleatorio&quot; o &quot;+ Manual&quot;.
                      </p>
                    )}
                    {(form.access_codes || []).map((c, idx) => {
                      const used = Number(c.used_count || 0);
                      const max = Number(c.max_uses || 0);
                      const exhausted = max > 0 && used >= max;
                      const nearFull = max > 0 && used >= Math.floor(max * 0.8) && !exhausted;
                      const publicLink = `${window.location.origin}/eventos/${editing?.id || "PENDING"}?code=${encodeURIComponent((c.code || "").toUpperCase())}`;
                      return (
                        <div key={idx} className={`bg-[#0F0F0F] border p-3 space-y-2 ${exhausted ? "border-red-500/40" : nearFull ? "border-amber-500/40" : "border-white/10"}`} data-testid={`access-code-row-${idx}`}>
                          <div className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-12 sm:col-span-3">
                              <label className="text-[10px] uppercase tracking-wider text-gray-500">Código *</label>
                              <input
                                value={c.code || ""}
                                onChange={e => setForm(p => ({ ...p, access_codes: p.access_codes.map((x, i) => i === idx ? { ...x, code: e.target.value.toUpperCase() } : x) }))}
                                placeholder="VIP2026"
                                className="w-full bg-[#1A1A1A] border border-white/10 p-1.5 text-xs text-white font-mono tracking-wider uppercase focus:border-[#FF4D00] outline-none"
                                data-testid={`access-code-input-${idx}`}
                                maxLength={24}
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-3">
                              <label className="text-[10px] uppercase tracking-wider text-gray-500">Etiqueta (opcional)</label>
                              <input
                                value={c.label || ""}
                                onChange={e => setForm(p => ({ ...p, access_codes: p.access_codes.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) }))}
                                placeholder="Ej: Familia"
                                className="w-full bg-[#1A1A1A] border border-white/10 p-1.5 text-xs text-white focus:border-[#FF4D00] outline-none"
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-2">
                              <label className="text-[10px] uppercase tracking-wider text-gray-500">Cupos máx</label>
                              <input
                                type="number"
                                min={0}
                                value={c.max_uses === 0 || c.max_uses ? c.max_uses : ""}
                                onChange={e => setForm(p => ({ ...p, access_codes: p.access_codes.map((x, i) => i === idx ? { ...x, max_uses: Number(e.target.value) || 0 } : x) }))}
                                placeholder="0 = ∞"
                                className="w-full bg-[#1A1A1A] border border-white/10 p-1.5 text-xs text-white focus:border-[#FF4D00] outline-none"
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-2">
                              <label className="text-[10px] uppercase tracking-wider text-gray-500">Vence</label>
                              <input
                                type="date"
                                value={c.expires_at || ""}
                                onChange={e => setForm(p => ({ ...p, access_codes: p.access_codes.map((x, i) => i === idx ? { ...x, expires_at: e.target.value } : x) }))}
                                className="w-full bg-[#1A1A1A] border border-white/10 p-1.5 text-xs text-white focus:border-[#FF4D00] outline-none"
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-2 flex items-center gap-2">
                              <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-400 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={c.active !== false}
                                  onChange={e => setForm(p => ({ ...p, access_codes: p.access_codes.map((x, i) => i === idx ? { ...x, active: e.target.checked } : x) }))}
                                />
                                Activo
                              </label>
                              <button
                                type="button"
                                onClick={() => setForm(p => ({ ...p, access_codes: p.access_codes.filter((_, i) => i !== idx) }))}
                                className="ml-auto text-red-400 hover:text-red-300"
                                title="Eliminar código"
                                data-testid={`access-code-remove-${idx}`}
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-[11px] pt-1 border-t border-white/5">
                            <span className={`font-bold ${exhausted ? "text-red-400" : nearFull ? "text-amber-400" : "text-green-400"}`} data-testid={`access-code-usage-${idx}`}>
                              Usos: {used}{max > 0 ? ` / ${max}` : " / ∞"}
                              {exhausted && " · AGOTADO"}
                              {nearFull && " · casi lleno"}
                            </span>
                            {editing && (c.code || "").trim() && (
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(publicLink).then(() => toast.success("Link copiado")); }}
                                className="text-blue-300 hover:text-blue-200 underline decoration-dotted"
                                data-testid={`access-code-copy-link-${idx}`}
                                title={publicLink}
                              >
                                Copiar link con código
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Documentos adjuntos solicitados en la inscripcion */}
            <div className="pt-3 border-t border-white/10" data-testid="event-form-attachments">
              <div className="flex items-center gap-2 mb-2">
                <Upload size={16} className="text-[#FF4D00]" />
                <label className="text-sm font-semibold">Documentos a adjuntar (opcional)</label>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                Solicita documentos al inscribirse: certificado médico, ticket de vuelo, foto de RUT, etc. Cada slot es un archivo. Max 5MB por archivo. Los archivos quedan asociados a la inscripción y el admin los descarga desde la tabla.
              </p>

              <div className="space-y-3">
                {(form.registration_attachments || []).map((slot, idx) => (
                  <div key={idx} className="bg-[#0F0F0F] border border-white/10 p-3" data-testid={`attachment-slot-${idx}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-[#FF4D00] w-6">#{idx + 1}</span>
                      <input
                        value={slot.label || ""}
                        onChange={e => {
                          const label = e.target.value;
                          const autoKey = label.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || `slot_${idx+1}`;
                          setForm(p => ({
                            ...p,
                            registration_attachments: p.registration_attachments.map((s, i) => i === idx ? { ...s, label, key: s.key || autoKey } : s),
                          }));
                        }}
                        placeholder="Etiqueta visible (ej: Certificado médico)"
                        className="flex-1 bg-[#1A1A1A] border border-white/10 h-9 px-2 text-sm text-white focus:border-[#FF4D00] outline-none"
                        data-testid={`attachment-slot-label-${idx}`}
                      />
                      <label className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer" data-testid={`attachment-slot-required-label-${idx}`}>
                        <input
                          type="checkbox"
                          checked={!!slot.required}
                          onChange={e => setForm(p => ({
                            ...p,
                            registration_attachments: p.registration_attachments.map((s, i) => i === idx ? { ...s, required: e.target.checked } : s),
                          }))}
                          className="accent-[#FF4D00]"
                          data-testid={`attachment-slot-required-${idx}`}
                        />
                        Obligatorio
                      </label>
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, registration_attachments: p.registration_attachments.filter((_, i) => i !== idx) }))}
                        className="text-red-400 hover:text-red-300 p-1"
                        data-testid={`attachment-slot-remove-${idx}`}
                        title="Eliminar slot"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                    <textarea
                      value={slot.description || ""}
                      onChange={e => setForm(p => ({
                        ...p,
                        registration_attachments: p.registration_attachments.map((s, i) => i === idx ? { ...s, description: e.target.value } : s),
                      }))}
                      rows={2}
                      placeholder="Instrucciones para el participante (opcional)"
                      className="w-full bg-[#1A1A1A] border border-white/10 p-2 text-xs text-white focus:border-[#FF4D00] outline-none whitespace-pre-wrap"
                      data-testid={`attachment-slot-description-${idx}`}
                    />
                    <input
                      value={slot.accept || ""}
                      onChange={e => setForm(p => ({
                        ...p,
                        registration_attachments: p.registration_attachments.map((s, i) => i === idx ? { ...s, accept: e.target.value } : s),
                      }))}
                      placeholder="Tipos aceptados (ej: .pdf,image/*) — vacío = cualquier archivo"
                      className="w-full bg-[#1A1A1A] border border-white/10 h-8 px-2 text-[11px] text-gray-300 focus:border-[#FF4D00] outline-none mt-2"
                      data-testid={`attachment-slot-accept-${idx}`}
                    />
                  </div>
                ))}
              </div>

              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-3 h-8 text-xs border-[#FF4D00]/40 text-[#FF4D00]"
                onClick={() => setForm(p => ({
                  ...p,
                  registration_attachments: [...(p.registration_attachments || []), { key: "", label: "", description: "", required: false, accept: "" }],
                }))}
                data-testid="attachment-slot-add"
              >
                <Plus size={12} className="mr-1" /> Agregar documento
              </Button>
            </div>

            {/* Event Photos (max 10) */}
            <div className="pt-3 border-t border-white/10">
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-semibold text-white">Fotos del Evento (max 10)</Label>
                <div className="flex gap-2">
                  <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" disabled={form.photos.length >= 10}
                    onChange={async (e) => { if (!e.target.files[0] || form.photos.length >= 10) return; const origFile = e.target.files[0]; const compressed = await compressImage(origFile); const fd = new FormData(); fd.append("file", compressed); try { const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } }); setForm(p => ({ ...p, photos: [...p.photos, data.url], image_url: p.image_url || data.url })); toast.success(`Foto subida (${formatBytes(origFile.size)} → ${formatBytes(compressed.size)})${form.image_url ? "" : " · Usada como portada"}`); } catch { toast.error("Error"); } }} />
                    <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs pointer-events-none"><Upload size={12} className="mr-1" /> Subir</Button>
                  </label>
                  <Button size="sm" variant="outline" className="border-white/20 text-gray-300 h-7 text-xs" disabled={form.photos.length >= 10}
                    onClick={() => setForm(p => ({ ...p, photos: [...p.photos, ""] }))}><Plus size={12} className="mr-1" /> URL</Button>
                </div>
              </div>
              {form.photos.length > 0 && <div className="flex flex-wrap gap-2 mb-2">{form.photos.map((ph, i) => {
                const isCover = ph && form.image_url === ph;
                return (
                  <div
                    key={i}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.currentTarget.style.opacity = "0.4"; }}
                    onDragEnd={(e) => { e.currentTarget.style.opacity = "1"; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = Number(e.dataTransfer.getData("text/plain"));
                      if (from === i) return;
                      setForm(p => {
                        const arr = [...p.photos];
                        const [moved] = arr.splice(from, 1);
                        arr.splice(i, 0, moved);
                        return { ...p, photos: arr };
                      });
                    }}
                    className={`relative w-16 h-12 bg-[#1A1A1A] border-2 cursor-move ${isCover ? "border-[#FF4D00]" : "border-white/5"}`}
                    title={isCover ? "Portada actual · arrastra para reordenar" : "Arrastra para reordenar · click ★ para portada"}
                    data-testid={`event-photo-${i}`}
                  >
                    {ph && <img src={ph.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${ph}` : ph} alt="" className="w-full h-full object-cover pointer-events-none" />}
                    {/* Flechas para reordenar en mobile */}
                    {i > 0 && <button type="button" title="Mover izquierda" className="absolute top-1/2 -left-1 -translate-y-1/2 bg-black/70 hover:bg-[#FF4D00] text-white rounded-full w-4 h-4 text-[9px] flex items-center justify-center leading-none md:hidden" onClick={() => setForm(p => { const arr = [...p.photos]; [arr[i-1], arr[i]] = [arr[i], arr[i-1]]; return { ...p, photos: arr }; })}>‹</button>}
                    {i < form.photos.length - 1 && <button type="button" title="Mover derecha" className="absolute top-1/2 -right-1 -translate-y-1/2 bg-black/70 hover:bg-[#FF4D00] text-white rounded-full w-4 h-4 text-[9px] flex items-center justify-center leading-none md:hidden" onClick={() => setForm(p => { const arr = [...p.photos]; [arr[i+1], arr[i]] = [arr[i], arr[i+1]]; return { ...p, photos: arr }; })}>›</button>}
                    <button
                      type="button"
                      title={isCover ? "Portada actual" : "Usar como portada"}
                      className={`absolute -bottom-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] leading-none ${isCover ? "bg-[#FF4D00] text-white" : "bg-black/80 text-gray-400 hover:text-[#FF4D00]"}`}
                      onClick={() => ph && setForm(p => ({ ...p, image_url: ph }))}
                      data-testid={`event-photo-set-cover-${i}`}
                    >★</button>
                    <button
                      type="button"
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center leading-none"
                      onClick={() => setForm(p => {
                        const newPhotos = p.photos.filter((_,idx) => idx !== i);
                        const newCover = p.image_url === ph ? (newPhotos[0] || "") : p.image_url;
                        return { ...p, photos: newPhotos, image_url: newCover };
                      })}
                    >×</button>
                  </div>
                );
              })}</div>}
              <p className="text-xs text-gray-500">{form.photos.length}/10 · <span className="text-[#FF4D00]">★</span> = portada · arrastra las fotos para reordenarlas</p>
            </div>

            {/* Distances Section */}
            <div className="pt-3 border-t border-white/10">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-sm font-semibold text-white">Distancias del Evento</Label>
                <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] hover:bg-[#FF4D00]/10 h-7 text-xs" onClick={addDistance} data-testid="admin-add-distance">
                  <Plus size={12} className="mr-1" /> Agregar Distancia
                </Button>
              </div>
              {form.distances.length === 0 && <p className="text-xs text-gray-500 mb-2">Sin distancias. Agrega distancias como 6K, 12K, 21K, etc.</p>}
              <div className="space-y-3">
                {form.distances.map((dist, idx) => (
                  <div key={idx} className="bg-[#1A1A1A] border border-white/5 p-3 space-y-2" data-testid={`admin-distance-row-${idx}`}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div><Label className="text-xs text-gray-500">Nombre (ej: 21K)</Label><Input className="bg-[#121212] border-white/10 mt-1 h-8 text-sm" value={dist.label} onChange={e => updateDistance(idx, "label", e.target.value)} placeholder="21K" data-testid={`admin-distance-label-${idx}`} /></div>
                        <div><Label className="text-xs text-gray-500">Km</Label><Input type="number" className="bg-[#121212] border-white/10 mt-1 h-8 text-sm" value={dist.km} onChange={e => updateDistance(idx, "km", e.target.value)} placeholder="21" /></div>
                        <div><Label className="text-xs text-gray-500">Precio (CLP)</Label><Input type="number" className="bg-[#121212] border-white/10 mt-1 h-8 text-sm" value={dist.price} onChange={e => updateDistance(idx, "price", e.target.value)} placeholder="25000" data-testid={`admin-distance-price-${idx}`} /></div>
                        <div><Label className="text-xs text-gray-500">Desnivel D+ (m)</Label><Input type="number" className="bg-[#121212] border-white/10 mt-1 h-8 text-sm" value={dist.elevation_gain || ""} onChange={e => updateDistance(idx, "elevation_gain", e.target.value)} placeholder="600" /></div>
                        <div>
                          <Label className="text-xs text-gray-500">Cupos (0=ilimitado)</Label>
                          <Input type="number" min="0" className="bg-[#121212] border-white/10 mt-1 h-8 text-sm" value={dist.max_participants || ""} onChange={e => updateDistance(idx, "max_participants", e.target.value)} placeholder="50" data-testid={`admin-distance-max-${idx}`} />
                          {typeof dist.registered_count === "number" && Number(dist.max_participants) > 0 && (
                            <p className={`text-[10px] mt-0.5 ${dist.is_full ? "text-red-400" : "text-gray-500"}`}>
                              {dist.registered_count}/{dist.max_participants} inscritos{dist.is_full ? " · AGOTADO" : ""}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300 mt-4 h-8 w-8" onClick={() => removeDistance(idx)}><Trash size={14} /></Button>
                    </div>
                    <div><Label className="text-xs text-gray-500">Descripcion de la ruta</Label><Textarea className="bg-[#121212] border-white/10 mt-1 text-sm min-h-[60px]" value={dist.description} onChange={e => updateDistance(idx, "description", e.target.value)} placeholder="Describe la ruta de esta distancia..." data-testid={`admin-distance-desc-${idx}`} /></div>
                    <div><Label className="text-xs text-gray-500">Mapa de la ruta (URL imagen o Google Maps embed)</Label><Input className="bg-[#121212] border-white/10 mt-1 h-8 text-sm" value={dist.map_url || ""} onChange={e => updateDistance(idx, "map_url", e.target.value)} placeholder="https://... (imagen del mapa o URL de Google Maps embed)" data-testid={`admin-distance-map-${idx}`} /></div>
                    <div className="flex items-center gap-2 mt-1">
                      <Label className="text-xs text-gray-500 flex-shrink-0">Fotos ({(dist.photos || []).length}/5):</Label>
                      <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" disabled={(dist.photos || []).length >= 5}
                        onChange={async (e) => { if (!e.target.files[0]) return; const compressed = await compressImage(e.target.files[0]); const fd = new FormData(); fd.append("file", compressed); try { const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } }); updateDistance(idx, "photos", [...(dist.photos || []), data.url]); toast.success("OK"); } catch { toast.error("Error"); } }} />
                        <span className="text-xs text-[#FF4D00] cursor-pointer hover:underline">+Subir</span>
                      </label>
                      {(dist.photos || []).length > 0 && <span className="text-xs text-red-400 cursor-pointer hover:underline" onClick={() => updateDistance(idx, "photos", (dist.photos || []).slice(0, -1))}>-Quitar</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* GPX Upload per distance */}
            {form.distances.length > 0 && (
              <div className="pt-3 border-t border-white/10">
                <Label className="text-sm font-semibold text-white mb-2 block">Upload GPX por Distancia</Label>
                <p className="text-xs text-gray-500 mb-3">Sube un archivo .gpx para generar mapa interactivo y perfil de desnivel automaticamente</p>
                <div className="space-y-2">
                  {form.distances.map((dist, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-[#1A1A1A] p-2 border border-white/5">
                      <span className="text-sm font-bold text-[#FF4D00] w-12">{dist.label || `#${idx+1}`}</span>
                      <input type="file" accept=".gpx" className="text-xs text-gray-400 file:mr-2 file:py-1 file:px-3 file:border-0 file:text-xs file:bg-[#FF4D00]/20 file:text-[#FF4D00] file:cursor-pointer"
                        data-testid={`admin-gpx-upload-${idx}`}
                        onChange={async (e) => {
                          const file = e.target.files[0]; if (!file) return;
                          if (!editing) { toast.error("Guarda el evento primero"); return; }
                          const fd = new FormData(); fd.append("file", file);
                          try {
                            const { data } = await axios.post(`${API}/events/${editing}/distances/${idx}/gpx`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
                            toast.success(`GPX procesado: ${data.distance_km}km, ${data.elevation_gain}m D+`);
                            // ⚠️ CRÍTICO: refrescar el formulario local con la distancia recién
                            // procesada en backend (incluye track_data, elevation_profile, etc).
                            // Sin esto, al guardar el evento sobreescribimos el GPX con arrays vacíos.
                            try {
                              const fresh = await apiGet(`/events/${editing}`);
                              const freshDistances = fresh?.distances || [];
                              setForm(p => ({
                                ...p,
                                distances: p.distances.map((d, i) => {
                                  if (i !== idx) return d;
                                  const fd2 = freshDistances[idx] || {};
                                  return {
                                    ...d,
                                    km: fd2.km !== undefined ? String(fd2.km) : d.km,
                                    elevation_gain: fd2.elevation_gain !== undefined ? fd2.elevation_gain : d.elevation_gain,
                                    track_data: fd2.track_data || [],
                                    elevation_profile: fd2.elevation_profile || [],
                                    markers: fd2.markers || d.markers || [],
                                  };
                                })
                              }));
                            } catch { /* el toast verde igual indica que el GPX se guardó */ }
                            load();
                          } catch (err) { toast.error(err.response?.data?.detail || "Error al procesar GPX"); }
                        }}
                      />
                      {dist.track_data && dist.track_data.length > 0 && <Badge className="bg-green-500/20 text-green-400 text-xs">Mapa cargado</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links of Interest */}
            <div className="pt-3 border-t border-white/10">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-sm font-semibold text-white">Links de Interes</Label>
                <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs"
                  onClick={() => setForm(p => ({ ...p, links: [...(p.links || []), { label: "", url: "" }] }))}>
                  <Plus size={12} className="mr-1" /> Agregar Link
                </Button>
              </div>
              {(form.links || []).map((link, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input className="bg-[#1A1A1A] border-white/10 h-8 text-sm flex-1" value={link.label} placeholder="Nombre del link"
                    onChange={e => { const links = [...(form.links || [])]; links[idx] = { ...links[idx], label: e.target.value }; setForm(p => ({ ...p, links })); }} />
                  <Input className="bg-[#1A1A1A] border-white/10 h-8 text-sm flex-[2]" value={link.url} placeholder="https://..."
                    onChange={e => { const links = [...(form.links || [])]; links[idx] = { ...links[idx], url: e.target.value }; setForm(p => ({ ...p, links })); }} />
                  <Button size="icon" variant="ghost" className="text-red-400 h-8 w-8"
                    onClick={() => setForm(p => ({ ...p, links: (p.links || []).filter((_, i) => i !== idx) }))}><Trash size={14} /></Button>
                </div>
              ))}
            </div>

            {/* Additional Info Section */}
            <div className="pt-3 border-t border-white/10">
              <Label className="text-sm font-semibold text-white">Ubicacion y Otros</Label>
              <div className="grid grid-cols-1 gap-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-gray-400">Latitud punto de partida</Label><Input type="number" step="any" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.start_lat || ""} onChange={e => setForm(p => ({ ...p, start_lat: e.target.value }))} placeholder="-45.57" /></div>
                  <div><Label className="text-xs text-gray-400">Longitud punto de partida</Label><Input type="number" step="any" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.start_lng || ""} onChange={e => setForm(p => ({ ...p, start_lng: e.target.value }))} placeholder="-72.07" /></div>
                </div>
                <div><Label className="text-xs text-gray-400">URL de Ubicacion (Google Maps)</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.location_url} onChange={e => setForm(p => ({ ...p, location_url: e.target.value }))} placeholder="https://www.google.com/maps/..." data-testid="event-form-location-url" /></div>
              </div>
            </div>

            {/* Suggested Products */}
            <div className="pt-3 border-t border-white/10">
              <Label className="text-sm font-semibold text-white mb-3 block">Material Sugerido (productos de la tienda)</Label>
              {allProducts.length === 0 ? (
                <p className="text-xs text-gray-500">No hay productos disponibles</p>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {allProducts.map(prod => {
                    const isSelected = form.suggested_products.includes(prod.id);
                    return (
                      <label key={prod.id} className={`flex items-center gap-2 p-2 border cursor-pointer transition-colors ${isSelected ? "bg-[#FF4D00]/10 border-[#FF4D00]/30" : "bg-[#1A1A1A] border-white/5 hover:border-white/20"}`}>
                        <input type="checkbox" checked={isSelected} className="accent-[#FF4D00]"
                          onChange={() => setForm(p => ({ ...p, suggested_products: isSelected ? p.suggested_products.filter(id => id !== prod.id) : [...p.suggested_products, prod.id] }))}
                        />
                        <img src={prod.image_url} alt="" className="w-8 h-8 object-cover flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{prod.name}</p>
                          <p className="text-xs text-[#FF4D00]">${prod.price.toLocaleString("es-CL")}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] font-bold mt-2" onClick={handleSave} data-testid="event-form-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Training Tab ──
function TrainingTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", level: "intermedio", duration_weeks: "8", sessions_per_week: "3", focus: "trail", image_url: "", price: "0", content_sections: [], coaches: [], badge_text: "", badge_color: "" });

  const load = useCallback(async () => { try { setItems(await apiGet("/training-plans")); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ title: "", description: "", level: "intermedio", duration_weeks: "8", sessions_per_week: "3", focus: "trail", image_url: "", price: "0", content_sections: [], coaches: [], badge_text: "", badge_color: "" });
  const openCreate = () => { resetForm(); setEditing(null); setOpen(true); };
  const openEdit = (item) => {
    setForm({ title: item.title, description: item.description, level: item.level, duration_weeks: String(item.duration_weeks), sessions_per_week: String(item.sessions_per_week), focus: item.focus, image_url: item.image_url || "", price: String(item.price || "0"), content_sections: item.content_sections || [], coaches: item.coaches || [], badge_text: item.badge_text || "", badge_color: item.badge_color || "" });
    setEditing(item.id); setOpen(true);
  };
  const handleSave = async () => {
    const body = { ...form, duration_weeks: Number(form.duration_weeks), sessions_per_week: Number(form.sessions_per_week), price: Number(form.price) || 0 };
    try { if (editing) { await apiPut(`/training-plans/${editing}`, body); toast.success("Actualizado"); } else { await apiPost("/training-plans", body); toast.success("Creado"); } setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const handleDelete = async (id) => { if (!window.confirm("Eliminar?")) return; try { await apiDelete(`/training-plans/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); } };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Entrenamientos ({items.length})</h3>
        <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500]" onClick={openCreate} data-testid="admin-add-training"><Plus size={14} className="mr-1" />Nuevo</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Titulo</TableHead><TableHead>Nivel</TableHead><TableHead>Duracion</TableHead><TableHead>Enfoque</TableHead><TableHead>Precio</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(p => (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.title}</TableCell>
              <TableCell><Badge variant="outline" className="text-xs">{p.level}</Badge></TableCell>
              <TableCell className="text-gray-400">{p.duration_weeks} sem / {p.sessions_per_week}x</TableCell>
              <TableCell className="text-gray-400">{(() => { const { Icon: FI, label: FL } = getFocusMeta(p.focus); return (<span className="inline-flex items-center gap-1"><FI size={14} weight="bold" />{FL}</span>); })()}</TableCell>
              <TableCell className="text-[#FF4D00]">{p.price ? `$${p.price.toLocaleString("es-CL")}` : "Gratis"}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDelete(p.id)}><Trash size={14} /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Plan" : "Nuevo Plan"}</DialogTitle><DialogDescription>Completa los campos</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-400">Titulo</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="training-form-title" /></div>
            <div><Label className="text-xs text-gray-400">Descripcion</Label><Textarea className="bg-[#1A1A1A] border-white/10 mt-1" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-gray-400">Nivel</Label>
                <Select value={form.level} onValueChange={v => setForm(p => ({ ...p, level: v }))}><SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="principiante">Principiante</SelectItem><SelectItem value="intermedio">Intermedio</SelectItem><SelectItem value="avanzado">Avanzado</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs text-gray-400">Semanas</Label><Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.duration_weeks} onChange={e => setForm(p => ({ ...p, duration_weeks: e.target.value }))} /></div>
              <div><Label className="text-xs text-gray-400">Sesiones/Sem</Label><Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.sessions_per_week} onChange={e => setForm(p => ({ ...p, sessions_per_week: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Enfoque</Label>
                <Select value={form.focus} onValueChange={v => setForm(p => ({ ...p, focus: v }))}><SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="trail">Trail</SelectItem><SelectItem value="ultra">Ultra</SelectItem><SelectItem value="running">Running</SelectItem><SelectItem value="montana">Montana</SelectItem><SelectItem value="triatlon">Triatlón</SelectItem><SelectItem value="natacion">Natación</SelectItem><SelectItem value="nutricion">Nutrición</SelectItem><SelectItem value="ciclismo">Ciclismo</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs text-gray-400">Precio (CLP)</Label><Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} /></div>
            </div>
            <div>
              <Label className="text-xs text-gray-400">Imagen de Portada</Label>
              <div className="flex flex-col sm:flex-row gap-2 mt-1">
                <Input
                  className="bg-[#1A1A1A] border-white/10 flex-1"
                  value={form.image_url}
                  onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
                  placeholder="Pega una URL o usa el boton para subir"
                  data-testid="training-form-image-url"
                />
                <label className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-[#FF4D00] hover:bg-[#E64500] text-white text-xs font-bold uppercase tracking-wider h-9 px-3 transition-colors shrink-0">
                  <Upload size={14} weight="bold" /> Subir
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="training-form-image-file"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const compressed = await compressImage(file);
                        const fd = new FormData();
                        fd.append("file", compressed);
                        const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
                        setForm(p => ({ ...p, image_url: data.url }));
                        toast.success(`Imagen subida (${formatBytes(file.size)} → ${formatBytes(compressed.size)})`);
                      } catch { toast.error("Error subiendo imagen"); }
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              {form.image_url && (
                <img src={form.image_url} alt="preview" className="mt-2 h-24 w-auto object-cover border border-white/10" />
              )}
            </div>

            {/* Viñeta destacable */}
            <div className="p-3 border border-white/10 bg-[#0F0F0F]" data-testid="training-form-badge-block">
              <Label className="text-xs text-gray-400">Viñeta destacada (opcional)</Label>
              <Input
                className="bg-[#1A1A1A] border-white/10 mt-1 uppercase"
                value={form.badge_text || ""}
                maxLength={12}
                onChange={e => setForm(p => ({ ...p, badge_text: e.target.value }))}
                placeholder="OFERTA · 30% · NUEVO"
                data-testid="training-form-badge-text"
              />
              <p className="text-[10px] text-gray-500 mt-1">Texto corto que aparece en la esquina de la imagen. Max 12 caracteres.</p>
              <BadgeColorPicker value={form.badge_color} onChange={c => setForm(p => ({ ...p, badge_color: c }))} />
              {form.badge_text && (
                <div className="mt-2 inline-block relative bg-[#1A1A1A] border border-white/10 p-2">
                  <span className="text-[10px] text-gray-500 mr-2">Vista previa:</span>
                  <span className="inline-block text-[10px] font-black uppercase tracking-wider px-2.5 py-1 shadow-lg" style={badgePreviewStyle(form.badge_color)}>{form.badge_text}</span>
                </div>
              )}
            </div>

            {/* Coaches (hasta 4) */}
            <div className="pt-3 border-t border-white/10" data-testid="training-coaches-section">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <Label className="text-sm font-semibold text-white">Profesionales / Coaches</Label>
                  <p className="text-[11px] text-gray-500">Hasta 4 coaches con foto y descripcion</p>
                </div>
                <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs" disabled={(form.coaches || []).length >= 4}
                  onClick={() => setForm(p => ({ ...p, coaches: [...(p.coaches || []), { name: "", role: "", photo_url: "", description: "" }] }))} data-testid="add-coach-btn">
                  <Plus size={12} className="mr-1" /> Agregar coach
                </Button>
              </div>
              {(form.coaches || []).map((c, ci) => (
                <div key={ci} className="bg-[#1A1A1A] border border-white/5 p-3 mb-2 space-y-2" data-testid={`coach-${ci}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-16 bg-[#0A0A0A] border border-white/10 flex-shrink-0 overflow-hidden">
                      {c.photo_url && <img src={c.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${c.photo_url}` : c.photo_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input className="bg-[#0A0A0A] border-white/10" placeholder="Nombre" value={c.name} onChange={e => setForm(p => ({ ...p, coaches: p.coaches.map((x, i) => i === ci ? { ...x, name: e.target.value } : x) }))} />
                      <Input className="bg-[#0A0A0A] border-white/10" placeholder="Rol (Coach principal, Asistente, Nutricionista...)" value={c.role || ""} onChange={e => setForm(p => ({ ...p, coaches: p.coaches.map((x, i) => i === ci ? { ...x, role: e.target.value } : x) }))} />
                    </div>
                    <Button size="icon" variant="ghost" className="text-red-400 h-7 w-7 flex-shrink-0" onClick={() => setForm(p => ({ ...p, coaches: p.coaches.filter((_, i) => i !== ci) }))} data-testid={`coach-delete-${ci}`}><Trash size={12} /></Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input className="bg-[#0A0A0A] border-white/10 flex-1 text-xs" placeholder="URL foto o sube imagen ↓" value={c.photo_url} onChange={e => setForm(p => ({ ...p, coaches: p.coaches.map((x, i) => i === ci ? { ...x, photo_url: e.target.value } : x) }))} />
                    <label className="cursor-pointer flex-shrink-0">
                      <input type="file" accept="image/*" className="hidden"
                        onChange={async (e) => {
                          if (!e.target.files[0]) return;
                          const compressed = await compressImage(e.target.files[0]);
                          const fd = new FormData(); fd.append("file", compressed);
                          try {
                            const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
                            setForm(p => ({ ...p, coaches: p.coaches.map((x, i) => i === ci ? { ...x, photo_url: data.url } : x) }));
                            toast.success(`OK (${formatBytes(e.target.files[0].size)} → ${formatBytes(compressed.size)})`);
                          } catch { toast.error("Error"); }
                          e.target.value = "";
                        }} />
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 border border-white/20 text-gray-300 hover:border-[#FF4D00] hover:text-[#FF4D00]"><Upload size={10} /> Foto</span>
                    </label>
                  </div>
                  <Textarea className="bg-[#0A0A0A] border-white/10 text-xs min-h-[60px]" placeholder="Descripcion / experiencia del coach" value={c.description} onChange={e => setForm(p => ({ ...p, coaches: p.coaches.map((x, i) => i === ci ? { ...x, description: e.target.value } : x) }))} />
                </div>
              ))}
            </div>

            {/* Content Sections */}
            <div className="pt-3 border-t border-white/10">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-sm font-semibold text-white">Secciones de Contenido</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs"
                    onClick={() => setForm(p => ({ ...p, content_sections: [...p.content_sections, { type: "text", title: "", content: "" }] }))}>
                    <Plus size={12} className="mr-1" /> Texto
                  </Button>
                  <Button size="sm" variant="outline" className="border-white/20 text-gray-300 h-7 text-xs"
                    onClick={() => setForm(p => ({ ...p, content_sections: [...p.content_sections, { type: "image", title: "", content: "" }] }))}>
                    <Plus size={12} className="mr-1" /> Imagen
                  </Button>
                </div>
              </div>
              {form.content_sections.map((sec, idx) => (
                <div key={idx} className="bg-[#1A1A1A] border border-white/5 p-3 mb-2">
                  <div className="flex justify-between items-center mb-2">
                    <Badge variant="outline" className="text-xs">{sec.type === "text" ? "Texto" : "Imagen"}</Badge>
                    <Button size="icon" variant="ghost" className="text-red-400 h-6 w-6"
                      onClick={() => setForm(p => ({ ...p, content_sections: p.content_sections.filter((_, i) => i !== idx) }))}><Trash size={12} /></Button>
                  </div>
                  <Input className="bg-[#121212] border-white/10 h-8 text-sm mb-2" value={sec.title} placeholder="Titulo (opcional)"
                    onChange={e => setForm(p => ({ ...p, content_sections: p.content_sections.map((s, i) => i === idx ? { ...s, title: e.target.value } : s) }))} />
                  {sec.type === "text" ? (
                    <Textarea className="bg-[#121212] border-white/10 text-sm min-h-[60px]" value={sec.content} placeholder="Contenido..."
                      onChange={e => setForm(p => ({ ...p, content_sections: p.content_sections.map((s, i) => i === idx ? { ...s, content: e.target.value } : s) }))} />
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input className="bg-[#121212] border-white/10 h-9 text-sm flex-1" value={sec.content} placeholder="Pega URL o usa Subir →"
                          onChange={e => setForm(p => ({ ...p, content_sections: p.content_sections.map((s, i) => i === idx ? { ...s, content: e.target.value } : s) }))} />
                        <label className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-[#FF4D00] hover:bg-[#E64500] text-white text-xs font-bold uppercase tracking-wider h-9 px-3 transition-colors shrink-0" data-testid={`training-section-upload-${idx}`}>
                          <Upload size={14} weight="bold" /> Subir
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const compressed = await compressImage(file);
                                const fd = new FormData();
                                fd.append("file", compressed);
                                const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
                                setForm(p => ({ ...p, content_sections: p.content_sections.map((s, i) => i === idx ? { ...s, content: data.url } : s) }));
                                toast.success(`Imagen subida (${formatBytes(file.size)} → ${formatBytes(compressed.size)})`);
                              } catch { toast.error("Error subiendo imagen"); }
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                      {sec.content && (
                        <img src={sec.content} alt="preview" className="h-24 w-auto object-cover border border-white/10" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] font-bold mt-2" onClick={handleSave} data-testid="training-form-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Products Tab ──
function ProductsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sortBy, setSortBy] = useState("created_desc");
  const [filterCat, setFilterCat] = useState("all");
  const initialForm = { name: "", description: "", price: "", category: "equipamiento", image_url: "", images: [], stock: "10", stock_by_size: {}, featured: false, badge_text: "", badge_color: "", color_variants: [], gender: "", sizes: [], full_description: "", features: [], specs: [], is_dropshipping: false, supplier_name: "", supplier_url: "", supplier_sku: "", supplier_cost: "0", supplier_email: "", supplier_whatsapp: "", shipping_days: "", shipping_origin: "" };
  const [form, setForm] = useState(initialForm);

  const load = useCallback(async () => { try { setItems(await apiGet("/admin/products")); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm(initialForm);
  const openCreate = () => { resetForm(); setEditing(null); setOpen(true); };
  const openEdit = (item) => {
    setForm({
      name: item.name, description: item.description, price: String(item.price), category: item.category,
      image_url: item.image_url || "", images: item.images || [], stock: String(item.stock), stock_by_size: item.stock_by_size || {}, featured: item.featured || false, badge_text: item.badge_text || "", badge_color: item.badge_color || "", color_variants: item.color_variants || [],
      gender: item.gender || "", sizes: item.sizes || [], full_description: item.full_description || "", features: item.features || [], specs: item.specs || [],
      is_dropshipping: item.is_dropshipping || false, supplier_name: item.supplier_name || "", supplier_url: item.supplier_url || "",
      supplier_sku: item.supplier_sku || "", supplier_cost: String(item.supplier_cost || "0"),
      supplier_email: item.supplier_email || "", supplier_whatsapp: item.supplier_whatsapp || "",
      shipping_days: item.shipping_days || "", shipping_origin: item.shipping_origin || ""
    });
    setEditing(item.id); setOpen(true);
  };
  const handleSave = async () => {
    if (form.images.length > 10) { toast.error("Maximo 10 fotos"); return; }
    const body = {
      ...form,
      price: Number(form.price) || 0,
      stock: Number(form.stock) || 0,
      stock_by_size: Object.fromEntries(
        (form.sizes || []).map(s => [s, Number((form.stock_by_size || {})[s]) || 0])
      ),
      supplier_cost: Number(form.supplier_cost) || 0,
      images: form.images.filter(Boolean)
    };
    try { if (editing) { await apiPut(`/products/${editing}`, body); toast.success("Actualizado"); } else { await apiPost("/products", body); toast.success("Creado"); } setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const handleDelete = async (id) => { if (!window.confirm("Eliminar?")) return; try { await apiDelete(`/products/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); } };

  const uploadPhoto = async (file) => {
    if (form.images.length >= 10) { toast.error("Maximo 10 fotos"); return; }
    const compressed = await compressImage(file);
    const fd = new FormData(); fd.append("file", compressed);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      setForm(p => ({ ...p, images: [...p.images, data.url], image_url: p.image_url || data.url }));
      const becameCover = !form.image_url;
      toast.success(`Foto subida (${formatBytes(file.size)} → ${formatBytes(compressed.size)})${becameCover ? " · Usada como portada" : ""}`);
    } catch { toast.error("Error subiendo foto"); }
  };

  // Filter + sort
  const filteredItems = filterCat === "all" ? items : items.filter(p => p.category === filterCat);
  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case "name_asc":     return (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" });
      case "name_desc":    return (b.name || "").localeCompare(a.name || "", "es", { sensitivity: "base" });
      case "price_asc":    return (a.price || 0) - (b.price || 0);
      case "price_desc":   return (b.price || 0) - (a.price || 0);
      case "stock_asc":    return (a.stock || 0) - (b.stock || 0);
      case "stock_desc":   return (b.stock || 0) - (a.stock || 0);
      case "category_asc": return (a.category || "").localeCompare(b.category || "", "es");
      case "created_asc":  return (a.created_at || "").localeCompare(b.created_at || "");
      default:             return (b.created_at || "").localeCompare(a.created_at || "");
    }
  });

  return (
    <div>
      <div className="flex justify-between items-start mb-4 gap-3 flex-wrap">
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Productos ({sortedItems.length}/{items.length})</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 h-9 text-xs w-40" data-testid="admin-products-filter-cat">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorias</SelectItem>
              <SelectItem value="zapatillas">Zapatillas</SelectItem>
              <SelectItem value="ropa">Ropa</SelectItem>
              <SelectItem value="equipamiento">Equipamiento</SelectItem>
              <SelectItem value="accesorios">Accesorios</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 h-9 text-xs w-48" data-testid="admin-products-sort">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">Mas recientes primero</SelectItem>
              <SelectItem value="created_asc">Mas antiguos primero</SelectItem>
              <SelectItem value="name_asc">A → Z (nombre)</SelectItem>
              <SelectItem value="name_desc">Z → A (nombre)</SelectItem>
              <SelectItem value="price_asc">Precio menor → mayor</SelectItem>
              <SelectItem value="price_desc">Precio mayor → menor</SelectItem>
              <SelectItem value="stock_asc">Stock menor → mayor</SelectItem>
              <SelectItem value="stock_desc">Stock mayor → menor</SelectItem>
              <SelectItem value="category_asc">Por categoria</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500]" onClick={openCreate} data-testid="admin-add-product"><Plus size={14} className="mr-1" />Nuevo</Button>
        </div>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Producto</TableHead><TableHead>Categoria</TableHead><TableHead>Precio</TableHead><TableHead>Stock</TableHead><TableHead>Tipo</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {sortedItems.map(p => (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.name} {p.featured && <Badge className="ml-2 bg-[#FF4D00]/20 text-[#FF4D00] text-xs">Destacado</Badge>}</TableCell>
              <TableCell className="text-gray-400">{p.category}</TableCell>
              <TableCell className="text-[#FF4D00]">${p.price.toLocaleString("es-CL")}</TableCell>
              <TableCell className={p.stock < 5 ? "text-red-400" : "text-gray-400"}>{p.stock}</TableCell>
              <TableCell>{p.is_dropshipping ? <Badge className="bg-blue-500/20 text-blue-400 text-xs">Dropshipping</Badge> : <span className="text-gray-500 text-xs">Stock propio</span>}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDelete(p.id)}><Trash size={14} /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Producto" : "Nuevo Producto"}</DialogTitle><DialogDescription>Datos basicos, fotos (hasta 10) y dropshipping</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {/* BASIC */}
            <div className="space-y-3">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Datos basicos</p>
              <div><Label className="text-xs text-gray-400">Nombre</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="product-form-name" /></div>
              <div><Label className="text-xs text-gray-400">Descripcion</Label><Textarea className="bg-[#1A1A1A] border-white/10 mt-1" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-gray-400">Precio venta (CLP)</Label><Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} data-testid="product-form-price" /></div>
                <div><Label className="text-xs text-gray-400">Stock</Label><Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: e.target.value }))} /></div>
                <div><Label className="text-xs text-gray-400">Categoria</Label>
                  <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}><SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="zapatillas">Zapatillas</SelectItem><SelectItem value="ropa">Ropa</SelectItem><SelectItem value="equipamiento">Equipamiento</SelectItem><SelectItem value="accesorios">Accesorios</SelectItem></SelectContent></Select></div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.featured} onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))} className="accent-[#FF4D00]" />
                <Label className="text-xs text-gray-400">Producto Destacado</Label>
              </div>
            </div>

            {/* Viñeta destacable */}
            <div className="p-3 border border-white/10 bg-[#0F0F0F]" data-testid="product-form-badge-block">
              <Label className="text-xs text-gray-400">Viñeta destacada (opcional)</Label>
              <Input
                className="bg-[#1A1A1A] border-white/10 mt-1 uppercase"
                value={form.badge_text || ""}
                maxLength={12}
                onChange={e => setForm(p => ({ ...p, badge_text: e.target.value }))}
                placeholder="OFERTA · 30% · NUEVO · 2x1"
                data-testid="product-form-badge-text"
              />
              <p className="text-[10px] text-gray-500 mt-1">Texto corto que aparece en la esquina de la imagen del producto. Max 12 caracteres.</p>
              <BadgeColorPicker value={form.badge_color} onChange={c => setForm(p => ({ ...p, badge_color: c }))} />
              {form.badge_text && (
                <div className="mt-2 inline-block relative bg-[#1A1A1A] border border-white/10 p-2">
                  <span className="text-[10px] text-gray-500 mr-2">Vista previa:</span>
                  <span className="inline-block text-[10px] font-black uppercase tracking-wider px-2.5 py-1 shadow-lg" style={badgePreviewStyle(form.badge_color)}>{form.badge_text}</span>
                </div>
              )}
            </div>

            {/* Variantes de color/diseño */}
            <div className="p-3 border border-white/10 bg-[#0F0F0F]" data-testid="product-form-color-variants">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div>
                  <Label className="text-xs text-gray-400">Variantes de color / diseño (opcional)</Label>
                  <p className="text-[10px] text-gray-500 mt-0.5">Permite mostrar el mismo producto en distintos colores. El cliente elige antes de comprar.</p>
                </div>
                <Button type="button" size="sm" variant="outline" className="border-[#FF4D00]/40 text-[#FF4D00] h-8 text-xs"
                  onClick={() => setForm(p => ({ ...p, color_variants: [...(p.color_variants || []), { name: "", hex: "#000000", images: [] }] }))}
                  data-testid="add-color-variant">
                  <Plus size={12} className="mr-1" /> Añadir color
                </Button>
              </div>
              {(form.color_variants || []).length === 0 && (
                <p className="text-[10px] text-gray-600 italic">Sin variantes — se mostrará una sola version del producto.</p>
              )}
              <div className="space-y-2 mt-2">
                {(form.color_variants || []).map((v, vIdx) => (
                  <div key={vIdx} className="bg-[#1A1A1A] border border-white/10 p-3 space-y-2" data-testid={`color-variant-${vIdx}`}>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={v.hex || "#000000"}
                        className="w-9 h-9 rounded-full cursor-pointer bg-transparent border-2 border-white/20 shrink-0"
                        onChange={e => setForm(p => ({ ...p, color_variants: p.color_variants.map((x, i) => i === vIdx ? { ...x, hex: e.target.value } : x) }))}
                      />
                      <Input
                        className="bg-[#0A0A0A] border-white/10 h-8 text-sm flex-1"
                        placeholder="Nombre (ej: Rojo, Verde militar, Negro)"
                        value={v.name || ""}
                        onChange={e => setForm(p => ({ ...p, color_variants: p.color_variants.map((x, i) => i === vIdx ? { ...x, name: e.target.value } : x) }))}
                      />
                      <Button type="button" size="icon" variant="ghost" className="text-red-400 h-8 w-8"
                        onClick={() => setForm(p => ({ ...p, color_variants: p.color_variants.filter((_, i) => i !== vIdx) }))}>
                        <Trash size={14} />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] text-gray-400 shrink-0">Stock:</Label>
                      <Input
                        type="number"
                        min="0"
                        className="bg-[#0A0A0A] border-white/10 h-8 text-sm w-24"
                        placeholder="Sin control"
                        value={v.stock ?? ""}
                        onChange={e => {
                          const raw = e.target.value;
                          setForm(p => ({ ...p, color_variants: p.color_variants.map((x, i) => i === vIdx ? { ...x, stock: raw === "" ? undefined : Number(raw) } : x) }));
                        }}
                        data-testid={`variant-stock-${vIdx}`}
                      />
                      <span className="text-[10px] text-gray-500">Vacio = sin control · 0 = agotado</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-[#FF4D00] hover:bg-[#E64500] text-white text-[11px] font-bold uppercase tracking-wider h-8 px-2.5 transition-colors shrink-0">
                        <Upload size={12} weight="bold" /> Subir foto
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            for (const file of files) {
                              if (((form.color_variants[vIdx]?.images || []).length) >= 6) { toast.error("Maximo 6 fotos por color"); break; }
                              try {
                                const compressed = await compressImage(file);
                                const fd = new FormData(); fd.append("file", compressed);
                                const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
                                setForm(p => ({ ...p, color_variants: p.color_variants.map((x, i) => i === vIdx ? { ...x, images: [...(x.images || []), data.url] } : x) }));
                              } catch { toast.error("Error subiendo"); }
                            }
                            if (files.length) toast.success(`${files.length} foto(s) agregada(s)`);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <span className="text-[10px] text-gray-500">{(v.images || []).length}/6 fotos · Primera = portada · Stock {v.stock === undefined ? "(sin control)" : v.stock}</span>
                    </div>
                    {(v.images || []).length > 0 && (
                      <div className="flex gap-1.5 flex-wrap">
                        {v.images.map((img, iIdx) => (
                          <div key={iIdx} className="relative group">
                            <img src={img} alt={`${v.name} ${iIdx+1}`} className={`w-14 h-14 object-cover border ${iIdx === 0 ? "border-[#FF4D00]" : "border-white/10"}`} />
                            <button
                              type="button"
                              onClick={() => setForm(p => ({ ...p, color_variants: p.color_variants.map((x, i) => i === vIdx ? { ...x, images: x.images.filter((_, j) => j !== iIdx) } : x) }))}
                              className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Eliminar foto"
                            >
                              <X size={10} weight="bold" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* DETALLE PRODUCTO (genero, tallas, descripcion larga, features, specs) */}
            <div className="space-y-3 pt-2 border-t border-white/5">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Detalle de pagina (opcional)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-400">Genero</Label>
                  <Select value={form.gender || "ninguno"} onValueChange={v => setForm(p => ({ ...p, gender: v === "ninguno" ? "" : v }))}>
                    <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1" data-testid="product-form-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ninguno">Sin especificar</SelectItem>
                      <SelectItem value="hombre">Hombre</SelectItem>
                      <SelectItem value="mujer">Mujer</SelectItem>
                      <SelectItem value="unisex">Unisex</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Tallas disponibles (XS-XL)</Label>
                  <div className="flex gap-1 mt-1.5" data-testid="product-form-sizes">
                    {["XS","S","M","L","XL"].map(s => {
                      const active = (form.sizes || []).includes(s);
                      return (
                        <button key={s} type="button"
                          onClick={() => setForm(p => ({ ...p, sizes: active ? p.sizes.filter(x => x !== s) : [...(p.sizes || []), s] }))}
                          className={`min-w-[2.5rem] h-9 text-xs font-bold border ${active ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "border-white/15 text-gray-400 hover:border-white/30"}`}
                          data-testid={`product-form-size-${s}`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Deja vacio si el producto no usa tallas</p>
                </div>
              </div>
              {(form.sizes || []).length > 0 && (
                <div className="p-3 border border-white/10 bg-[#0F0F0F]" data-testid="product-form-stock-by-size">
                  <Label className="text-xs text-gray-400">Stock por talla (visible solo para ti)</Label>
                  <p className="text-[10px] text-gray-500 mb-2">Independiente del Stock total. Cliente solo ve "Disponible" o "Agotado" por talla.</p>
                  <div className="grid grid-cols-5 gap-2">
                    {(form.sizes || []).map(s => (
                      <div key={s} className="text-center">
                        <Label className="text-[11px] text-[#FF4D00] font-bold">{s}</Label>
                        <Input
                          type="number"
                          min="0"
                          className="bg-[#1A1A1A] border-white/10 mt-1 h-8 text-xs text-center"
                          value={(form.stock_by_size || {})[s] ?? ""}
                          placeholder="0"
                          onChange={e => setForm(p => ({ ...p, stock_by_size: { ...(p.stock_by_size || {}), [s]: e.target.value } }))}
                          data-testid={`stock-by-size-${s}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-gray-400">Descripcion completa (pagina detalle)</Label>
                <Textarea className="bg-[#1A1A1A] border-white/10 mt-1 min-h-[100px]" value={form.full_description} onChange={e => setForm(p => ({ ...p, full_description: e.target.value }))} placeholder="Descripcion larga, parrafos, puedes usar saltos de linea." data-testid="product-form-fulldesc" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-gray-400">Caracteristicas (bullets)</Label>
                  <Button size="sm" variant="ghost" className="h-6 text-xs text-[#FF4D00]" onClick={() => setForm(p => ({ ...p, features: [...(p.features || []), ""] }))} data-testid="product-form-add-feature">+ Agregar</Button>
                </div>
                {(form.features || []).map((f, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <Input className="bg-[#1A1A1A] border-white/10 text-xs flex-1" value={f} onChange={e => setForm(p => ({ ...p, features: p.features.map((x, idx) => idx === i ? e.target.value : x) }))} placeholder="Ej: Impermeable" />
                    <button type="button" className="text-red-400 hover:text-red-300" onClick={() => setForm(p => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }))}><X size={14} /></button>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-gray-400">Especificaciones (clave / valor)</Label>
                  <Button size="sm" variant="ghost" className="h-6 text-xs text-[#FF4D00]" onClick={() => setForm(p => ({ ...p, specs: [...(p.specs || []), { label: "", value: "" }] }))} data-testid="product-form-add-spec">+ Agregar</Button>
                </div>
                {(form.specs || []).map((sp, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mt-1">
                    <Input className="bg-[#1A1A1A] border-white/10 text-xs" value={sp.label} onChange={e => setForm(p => ({ ...p, specs: p.specs.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x) }))} placeholder="Ej: Peso" />
                    <Input className="bg-[#1A1A1A] border-white/10 text-xs" value={sp.value} onChange={e => setForm(p => ({ ...p, specs: p.specs.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x) }))} placeholder="Ej: 320 g" />
                    <button type="button" className="text-red-400 hover:text-red-300" onClick={() => setForm(p => ({ ...p, specs: p.specs.filter((_, idx) => idx !== i) }))}><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* PHOTOS */}
            <div className="space-y-2 pt-2 border-t border-white/5">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Fotos del producto (hasta 10)</p>
              <div><Label className="text-xs text-gray-400">URL Imagen Principal</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://... (se usa como portada)" /></div>
              <div className="flex items-center gap-2 mt-2">
                <Label className="text-xs text-gray-400">Galeria adicional ({form.images.length}/10):</Label>
                <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" disabled={form.images.length >= 10}
                  onChange={async (e) => { if (!e.target.files[0]) return; await uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 border ${form.images.length >= 10 ? "border-white/10 text-gray-600 cursor-not-allowed" : "border-white/20 text-gray-300 hover:border-[#FF4D00] hover:text-[#FF4D00]"}`}><Upload size={12} /> Subir</span>
                </label>
                <Button type="button" size="sm" variant="outline" className="border-white/20 text-gray-300 h-7 text-xs" disabled={form.images.length >= 10}
                  onClick={() => setForm(p => ({ ...p, images: [...p.images, ""] }))}><Plus size={12} className="mr-1" /> URL</Button>
              </div>
              {form.images.length > 0 && (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {form.images.map((img, i) => {
                    const isCover = img && form.image_url === img;
                    return (
                      <div
                        key={i}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.currentTarget.style.opacity = "0.4"; }}
                        onDragEnd={(e) => { e.currentTarget.style.opacity = "1"; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = Number(e.dataTransfer.getData("text/plain"));
                          if (from === i) return;
                          setForm(p => {
                            const arr = [...p.images];
                            const [moved] = arr.splice(from, 1);
                            arr.splice(i, 0, moved);
                            return { ...p, images: arr };
                          });
                        }}
                        className="flex items-center gap-2 cursor-move"
                        data-testid={`product-photo-row-${i}`}
                      >
                        <div className="flex-shrink-0 text-gray-500 px-1 select-none" title="Arrastra para reordenar">⋮⋮</div>
                        <div className={`relative w-12 h-12 bg-[#1A1A1A] border-2 ${isCover ? "border-[#FF4D00]" : "border-white/5"} flex-shrink-0 overflow-hidden`}>
                          {img && <img src={img.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img} alt="" className="w-full h-full object-cover pointer-events-none" />}
                        </div>
                        <button
                          type="button"
                          title={isCover ? "Portada actual" : "Usar como portada"}
                          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs ${isCover ? "bg-[#FF4D00] text-white" : "bg-[#1A1A1A] text-gray-500 hover:text-[#FF4D00]"}`}
                          onClick={() => img && setForm(p => ({ ...p, image_url: img }))}
                          data-testid={`product-photo-set-cover-${i}`}
                        >★</button>
                        <div className="flex flex-col gap-0.5 md:hidden flex-shrink-0">
                          {i > 0 && <button type="button" title="Subir" className="bg-black/70 hover:bg-[#FF4D00] text-white w-5 h-4 text-[10px] leading-none rounded" onClick={() => setForm(p => { const arr = [...p.images]; [arr[i-1], arr[i]] = [arr[i], arr[i-1]]; return { ...p, images: arr }; })}>↑</button>}
                          {i < form.images.length - 1 && <button type="button" title="Bajar" className="bg-black/70 hover:bg-[#FF4D00] text-white w-5 h-4 text-[10px] leading-none rounded" onClick={() => setForm(p => { const arr = [...p.images]; [arr[i+1], arr[i]] = [arr[i], arr[i+1]]; return { ...p, images: arr }; })}>↓</button>}
                        </div>
                        <Input className="bg-[#1A1A1A] border-white/10 text-xs flex-1" value={img} placeholder="https://..." onChange={e => setForm(p => ({ ...p, images: p.images.map((x, idx) => idx === i ? e.target.value : x) }))} />
                        <button type="button" className="text-red-400 hover:text-red-300" onClick={() => setForm(p => {
                          const newImgs = p.images.filter((_, idx) => idx !== i);
                          const newCover = p.image_url === img ? (newImgs[0] || "") : p.image_url;
                          return { ...p, images: newImgs, image_url: newCover };
                        })}><X size={14} /></button>
                      </div>
                    );
                  })}
                  <p className="text-xs text-gray-500 italic pt-1">Arrastra las fotos (icono ⋮⋮) para reordenarlas · la portada ★ aparece primero en la tienda</p>
                </div>
              )}
            </div>

            {/* DROPSHIPPING */}
            <div className="space-y-3 pt-2 border-t border-white/5">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Dropshipping</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_dropshipping} onChange={e => setForm(p => ({ ...p, is_dropshipping: e.target.checked }))} className="accent-[#FF4D00]" data-testid="product-form-dropshipping" />
                  <span className="text-xs text-gray-300">Es dropshipping</span>
                </label>
              </div>
              {form.is_dropshipping && (
                <div className="space-y-3 bg-blue-500/5 border border-blue-500/20 p-3">
                  <p className="text-xs text-blue-400/80 leading-relaxed">Datos privados del proveedor (solo visibles para el admin). Cuando un cliente compre, podras ver de donde pedir el articulo.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-gray-400">Proveedor</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.supplier_name} onChange={e => setForm(p => ({ ...p, supplier_name: e.target.value }))} placeholder="AliExpress / Decathlon / Amazon" /></div>
                    <div><Label className="text-xs text-gray-400">SKU del proveedor</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.supplier_sku} onChange={e => setForm(p => ({ ...p, supplier_sku: e.target.value }))} placeholder="ID o codigo en el proveedor" /></div>
                  </div>
                  <div><Label className="text-xs text-gray-400">URL del producto en el proveedor</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.supplier_url} onChange={e => setForm(p => ({ ...p, supplier_url: e.target.value }))} placeholder="https://..." /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-gray-400">Email proveedor</Label><Input type="email" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.supplier_email} onChange={e => setForm(p => ({ ...p, supplier_email: e.target.value }))} placeholder="ventas@proveedor.com" /></div>
                    <div><Label className="text-xs text-gray-400">WhatsApp proveedor</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.supplier_whatsapp} onChange={e => setForm(p => ({ ...p, supplier_whatsapp: e.target.value }))} placeholder="+86 138 ..." /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs text-gray-400">Costo proveedor (CLP)</Label><Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.supplier_cost} onChange={e => setForm(p => ({ ...p, supplier_cost: e.target.value }))} placeholder="0" />
                      {Number(form.supplier_cost) > 0 && Number(form.price) > 0 && (
                        <p className="text-[10px] text-emerald-400 mt-1">Margen: ${(Number(form.price) - Number(form.supplier_cost)).toLocaleString("es-CL")} ({Math.round((1 - Number(form.supplier_cost) / Number(form.price)) * 100)}%)</p>
                      )}
                    </div>
                    <div><Label className="text-xs text-gray-400">Tiempo envio</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.shipping_days} onChange={e => setForm(p => ({ ...p, shipping_days: e.target.value }))} placeholder="7-15 dias" /></div>
                    <div><Label className="text-xs text-gray-400">Origen envio</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.shipping_origin} onChange={e => setForm(p => ({ ...p, shipping_origin: e.target.value }))} placeholder="China, EE.UU., Chile" /></div>
                  </div>
                </div>
              )}
            </div>

            <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] font-bold mt-2" onClick={handleSave} data-testid="product-form-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Orders Tab ──
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [detailOrder, setDetailOrder] = useState(null);
  const [supplierOrder, setSupplierOrder] = useState(null);  // {item, prod, message}
  const load = useCallback(async () => { try { setOrders(await apiGet("/orders")); } catch {} }, []);
  useEffect(() => { load(); apiGet("/admin/products").then(setProducts).catch(() => {}); }, [load]);

  const updateStatus = async (id, status) => {
    try { await axios.put(`${API}/orders/${id}/status`, { status }, { headers: getHeaders() }); toast.success("Estado actualizado"); load(); } catch { toast.error("Error"); }
  };

  const markSupplierStatus = async (orderId, itemIndex, newStatus, supplierName) => {
    try {
      const { data } = await axios.put(`${API}/orders/${orderId}/supplier-status`, { item_index: itemIndex, status: newStatus, supplier_name: supplierName || "" }, { headers: getHeaders() });
      toast.success(newStatus === "ordered" ? "Marcado como enviado al proveedor" : newStatus === "received" ? "Marcado como recibido" : "Marcado como pendiente");
      // Update local state without full reload
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, supplier_status: data.supplier_status } : o));
      if (detailOrder && detailOrder.id === orderId) setDetailOrder(p => ({ ...p, supplier_status: data.supplier_status }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const productById = (id) => products.find(p => p.id === id);

  const buildSupplierMessage = (order, item, prod) => {
    const isPickup = order.delivery_method === "pickup";
    const lines = [
      `Hola ${prod.supplier_name || "proveedor"}, necesito hacer el siguiente pedido:`,
      ``,
      `PRODUCTO`,
      `- Nombre: ${item.name}`,
      `- SKU: ${prod.supplier_sku || "(sin codigo)"}`,
      `- Cantidad: ${item.quantity}`,
      `- URL referencia: ${prod.supplier_url || "—"}`,
      ``,
      `ENVIAR A:`,
    ];
    if (isPickup) {
      lines.push(`- Modalidad: Retiro por Pro Vertical en evento "${order.pickup_event_name || ""}"`);
      lines.push(`- Direccion de retiro Pro Vertical: por favor enviar a nuestra base en Coyhaique, Aysen, Chile.`);
    } else {
      lines.push(`- Nombre: ${order.customer_name}`);
      lines.push(`- Telefono: ${order.customer_phone}`);
      lines.push(`- Email: ${order.customer_email}`);
      lines.push(`- Direccion: ${order.shipping_address || ""}`);
      lines.push(`- Ciudad: ${order.shipping_city || ""}, ${order.shipping_region || ""}`);
      if (order.shipping_notes) lines.push(`- Notas: ${order.shipping_notes}`);
    }
    lines.push(``);
    lines.push(`Referencia interna pedido: #${order.id.slice(0, 8)}`);
    lines.push(`Saludos, Pro Vertical`);
    return lines.join("\n");
  };

  const openSupplierOrder = (item, prod) => {
    if (!detailOrder) return;
    setSupplierOrder({
      item,
      prod,
      message: buildSupplierMessage(detailOrder, item, prod),
      subject: `Pedido Pro Vertical #${detailOrder.id.slice(0, 8)} - ${item.name} (SKU ${prod.supplier_sku || "—"})`
    });
  };

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(supplierOrder.message); toast.success("Mensaje copiado al portapapeles"); }
    catch { toast.error("No se pudo copiar - selecciona el texto y copia manual"); }
  };

  const sendByEmail = () => {
    const to = supplierOrder.prod.supplier_email || "";
    const url = `mailto:${to}?subject=${encodeURIComponent(supplierOrder.subject)}&body=${encodeURIComponent(supplierOrder.message)}`;
    window.open(url, "_blank");
  };

  const sendByWhatsApp = () => {
    const phone = (supplierOrder.prod.supplier_whatsapp || "").replace(/\D/g, "");
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(supplierOrder.message)}`
      : `https://wa.me/?text=${encodeURIComponent(supplierOrder.message)}`;
    window.open(url, "_blank");
  };

  const statusColors = { pending: "text-yellow-400 border-yellow-400/30", completed: "text-green-400 border-green-400/30", paid: "text-emerald-400 border-emerald-400/30", failed: "text-red-400 border-red-400/30", cancelled: "text-gray-400 border-gray-400/30", refunded: "text-orange-400 border-orange-400/30" };

  // ── Filters ──
  const [filterType, setFilterType] = useState("all"); // all | dropshipping | local
  const [filterSupplierStatus, setFilterSupplierStatus] = useState("all"); // all | pending | ordered | received

  const orderDropInfo = (o) => {
    const dropItems = (o.items || []).map((it, idx) => ({ idx, prod: productById(it.product_id) })).filter(x => x.prod?.is_dropshipping);
    if (dropItems.length === 0) return { kind: "local", pendingCount: 0, orderedCount: 0, receivedCount: 0, totalDrop: 0 };
    let pendingCount = 0, orderedCount = 0, receivedCount = 0;
    for (const x of dropItems) {
      const s = (o.supplier_status || {})[String(x.idx)]?.status;
      if (s === "ordered") orderedCount++;
      else if (s === "received") receivedCount++;
      else pendingCount++;
    }
    return { kind: "dropshipping", pendingCount, orderedCount, receivedCount, totalDrop: dropItems.length };
  };

  const filtered = orders.filter(o => {
    const info = orderDropInfo(o);
    if (filterType === "dropshipping" && info.kind !== "dropshipping") return false;
    if (filterType === "local" && info.kind !== "local") return false;
    if (filterSupplierStatus !== "all") {
      if (info.kind !== "dropshipping") return false;
      if (filterSupplierStatus === "pending" && info.pendingCount === 0) return false;
      if (filterSupplierStatus === "ordered" && info.orderedCount === 0) return false;
      if (filterSupplierStatus === "received" && !(info.receivedCount === info.totalDrop)) return false;
      if (filterSupplierStatus === "action_needed" && (info.pendingCount + info.orderedCount) === 0) return false;
    }
    return true;
  });

  const stats = {
    total: orders.length,
    drop: orders.filter(o => orderDropInfo(o).kind === "dropshipping").length,
    local: orders.filter(o => orderDropInfo(o).kind === "local").length,
    actionNeeded: orders.filter(o => { const i = orderDropInfo(o); return i.kind === "dropshipping" && (i.pendingCount + i.orderedCount) > 0; }).length,
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Pedidos ({filtered.length}{filtered.length !== orders.length ? ` de ${orders.length}` : ""})</h3>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-44 h-9 text-xs" data-testid="orders-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tipo: Todos ({stats.total})</SelectItem>
              <SelectItem value="dropshipping">Solo dropshipping ({stats.drop})</SelectItem>
              <SelectItem value="local">Solo stock propio ({stats.local})</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterSupplierStatus} onValueChange={setFilterSupplierStatus}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-56 h-9 text-xs" data-testid="orders-filter-supplier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Estado proveedor: Todos</SelectItem>
              <SelectItem value="action_needed">⚡ Pendientes de gestionar ({stats.actionNeeded})</SelectItem>
              <SelectItem value="pending">Solo con items sin pedir</SelectItem>
              <SelectItem value="ordered">Solo con items pedidos</SelectItem>
              <SelectItem value="received">Solo completamente recibidos</SelectItem>
            </SelectContent>
          </Select>
          {(filterType !== "all" || filterSupplierStatus !== "all") && (
            <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-9 text-xs" onClick={() => { setFilterType("all"); setFilterSupplierStatus("all"); }} data-testid="orders-clear-filters">Limpiar</Button>
          )}
        </div>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Cliente</TableHead><TableHead>Total</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {filtered.map(o => {
            const dropItems = (o.items || []).map((it, idx) => ({ it, idx, prod: productById(it.product_id) })).filter(x => x.prod?.is_dropshipping);
            const dropOrderedCount = dropItems.filter(x => (o.supplier_status || {})[String(x.idx)]?.status === "ordered" || (o.supplier_status || {})[String(x.idx)]?.status === "received").length;
            const hasDrop = dropItems.length > 0;
            return (
              <TableRow key={o.id}>
                <TableCell className="font-mono text-xs">{o.id.slice(0, 8)}{hasDrop && <Badge className={`ml-2 text-[10px] ${dropOrderedCount === dropItems.length ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-500/20 text-blue-400"}`}>DS {dropOrderedCount}/{dropItems.length}</Badge>}</TableCell>
                <TableCell><p className="font-medium">{o.customer_name}</p><p className="text-xs text-gray-500">{o.customer_email}</p></TableCell>
                <TableCell className="text-[#FF4D00] font-bold">${o.total.toLocaleString("es-CL")}</TableCell>
                <TableCell><Badge variant="outline" className={`text-xs ${statusColors[o.status] || ""}`}>{o.status}</Badge></TableCell>
                <TableCell className="text-gray-400 text-xs">{new Date(o.created_at).toLocaleString("es-CL")}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="border-white/10 h-8 text-xs" onClick={() => setDetailOrder(o)} data-testid={`order-detail-${o.id}`}>Ver</Button>
                    <Select value={o.status} onValueChange={v => updateStatus(o.id, v)}>
                      <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="paid">Pagado</SelectItem>
                        <SelectItem value="completed">Completado</SelectItem>
                        <SelectItem value="failed">Fallido</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                        <SelectItem value="refunded">Reembolsado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {orders.length === 0 && <p className="text-center text-gray-400 py-8">No hay pedidos</p>}
      {orders.length > 0 && filtered.length === 0 && <p className="text-center text-gray-400 py-8">No hay pedidos que coincidan con los filtros</p>}

      {/* Order Detail Dialog */}
      <Dialog open={!!detailOrder} onOpenChange={(o) => !o && setDetailOrder(null)}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle>Pedido #{detailOrder?.id.slice(0, 8)}</DialogTitle>
            <DialogDescription>{detailOrder && new Date(detailOrder.created_at).toLocaleString("es-CL")}</DialogDescription>
          </DialogHeader>
          {detailOrder && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs bg-[#1A1A1A] p-3 border border-white/5">
                <div><p className="text-gray-500">Cliente</p><p className="font-medium">{detailOrder.customer_name}</p></div>
                <div><p className="text-gray-500">Email</p><p>{detailOrder.customer_email}</p></div>
                <div><p className="text-gray-500">Telefono</p><p>{detailOrder.customer_phone}</p></div>
                <div><p className="text-gray-500">Metodo</p><p>{
                  detailOrder.delivery_method === "shipping" ? `Envio: ${detailOrder.shipping_address}, ${detailOrder.shipping_city}` :
                  detailOrder.delivery_method === "coordinate" ? `Coordinar con vendedor — Lugar: ${detailOrder.shipping_address || "—"} · Cuando: ${detailOrder.shipping_notes || "—"}` :
                  detailOrder.delivery_method === "store_pickup" ? "Retiro en punto de retiro" :
                  `Retiro: ${detailOrder.pickup_event_name || "—"}`
                }</p></div>
                {detailOrder.mp_payment_id && <div><p className="text-gray-500">MP Payment ID</p><p className="font-mono">{detailOrder.mp_payment_id}</p></div>}
              </div>

              <div>
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-2">Items</p>
                <div className="space-y-2">
                  {(detailOrder.items || []).map((it, i) => {
                    const prod = productById(it.product_id);
                    const supStatus = (detailOrder.supplier_status || {})[String(i)] || {};
                    const sStatus = supStatus.status || "pending";
                    return (
                      <div key={i} className="bg-[#1A1A1A] border border-white/5 p-3">
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1">
                            <p className="font-medium">{it.name} <span className="text-gray-500 text-xs">x{it.quantity}</span></p>
                            <p className="text-xs text-[#FF4D00]">${(it.price || 0).toLocaleString("es-CL")} c/u</p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {prod?.is_dropshipping && <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">Dropshipping</Badge>}
                            {prod?.is_dropshipping && sStatus === "ordered" && <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Enviado al proveedor</Badge>}
                            {prod?.is_dropshipping && sStatus === "received" && <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">Recibido</Badge>}
                            {prod?.is_dropshipping && sStatus === "pending" && <Badge variant="outline" className="border-yellow-400/30 text-yellow-400 text-[10px]">Pendiente proveedor</Badge>}
                          </div>
                        </div>
                        {prod?.is_dropshipping && (
                          <div className="mt-2 pt-2 border-t border-white/5 text-xs space-y-1">
                            {prod.supplier_name && <p className="text-gray-400">Proveedor: <span className="text-gray-200">{prod.supplier_name}</span>{prod.supplier_sku && ` (SKU: ${prod.supplier_sku})`}</p>}
                            {prod.supplier_cost > 0 && <p className="text-gray-400">Costo: <span className="text-gray-200">${prod.supplier_cost.toLocaleString("es-CL")}</span> · Margen: <span className="text-emerald-400">${(prod.price - prod.supplier_cost).toLocaleString("es-CL")}</span></p>}
                            {(prod.shipping_days || prod.shipping_origin) && <p className="text-gray-400">Envio: {[prod.shipping_days, prod.shipping_origin].filter(Boolean).join(" desde ")}</p>}
                            {supStatus.ordered_at && <p className="text-emerald-400/80 text-[11px]">Pedido al proveedor: {new Date(supStatus.ordered_at).toLocaleString("es-CL")}</p>}
                            {supStatus.received_at && <p className="text-purple-400/80 text-[11px]">Recibido: {new Date(supStatus.received_at).toLocaleString("es-CL")}</p>}
                            {prod.supplier_url && <a href={prod.supplier_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-blue-400 hover:underline">→ Ir al proveedor a comprar</a>}
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white text-xs h-8" onClick={() => openSupplierOrder(it, prod)} data-testid={`supplier-order-btn-${i}`}>
                                Generar pedido al proveedor
                              </Button>
                              {sStatus !== "ordered" && sStatus !== "received" && (
                                <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-8" onClick={() => markSupplierStatus(detailOrder.id, i, "ordered", prod.supplier_name)} data-testid={`supplier-mark-ordered-${i}`}>
                                  ✓ Marcar enviado al proveedor
                                </Button>
                              )}
                              {sStatus === "ordered" && (
                                <>
                                  <Button size="sm" variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 text-xs h-8" onClick={() => markSupplierStatus(detailOrder.id, i, "received", prod.supplier_name)} data-testid={`supplier-mark-received-${i}`}>
                                    ✓ Marcar recibido
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-gray-400 hover:text-yellow-400 text-xs h-8" onClick={() => markSupplierStatus(detailOrder.id, i, "pending")} data-testid={`supplier-mark-pending-${i}`}>
                                    Deshacer
                                  </Button>
                                </>
                              )}
                              {sStatus === "received" && (
                                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-yellow-400 text-xs h-8" onClick={() => markSupplierStatus(detailOrder.id, i, "pending")} data-testid={`supplier-mark-pending-${i}`}>
                                  Deshacer
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>${(detailOrder.subtotal || detailOrder.total).toLocaleString("es-CL")}</span></div>
                {detailOrder.discount && <div className="flex justify-between"><span className="text-emerald-400">Descuento ({detailOrder.discount.code})</span><span>-${detailOrder.discount.amount.toLocaleString("es-CL")}</span></div>}
                <div className="flex justify-between font-bold text-base pt-1 border-t border-white/10"><span>Total</span><span className="text-[#FF4D00]">${detailOrder.total.toLocaleString("es-CL")}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Supplier Order Dialog */}
      <Dialog open={!!supplierOrder} onOpenChange={(o) => !o && setSupplierOrder(null)}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-2xl" data-testid="supplier-order-dialog">
          <DialogHeader>
            <DialogTitle>Pedido al proveedor</DialogTitle>
            <DialogDescription>
              {supplierOrder && `${supplierOrder.prod.supplier_name || "Proveedor"} · ${supplierOrder.item.name} x${supplierOrder.item.quantity}`}
            </DialogDescription>
          </DialogHeader>
          {supplierOrder && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-400">Asunto del email</Label>
                <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={supplierOrder.subject} onChange={e => setSupplierOrder(p => ({ ...p, subject: e.target.value }))} data-testid="supplier-order-subject" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Mensaje (editable antes de enviar)</Label>
                <Textarea className="bg-[#1A1A1A] border-white/10 mt-1 min-h-[260px] font-mono text-xs" value={supplierOrder.message} onChange={e => setSupplierOrder(p => ({ ...p, message: e.target.value }))} data-testid="supplier-order-message" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold flex-1 min-w-[140px]" onClick={copyMessage} data-testid="supplier-order-copy">Copiar mensaje</Button>
                <Button variant="outline" className="border-white/10 flex-1 min-w-[140px]" onClick={sendByEmail} data-testid="supplier-order-email">Enviar por Email</Button>
                <Button variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/10 flex-1 min-w-[140px]" onClick={sendByWhatsApp} data-testid="supplier-order-wa">Enviar por WhatsApp</Button>
                {supplierOrder.prod.supplier_url && (
                  <Button variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 flex-1 min-w-[140px]" onClick={() => window.open(supplierOrder.prod.supplier_url, "_blank")} data-testid="supplier-order-open">Abrir tienda proveedor</Button>
                )}
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold" onClick={() => { const idx = (detailOrder.items || []).findIndex(x => x.product_id === supplierOrder.item.product_id); markSupplierStatus(detailOrder.id, idx, "ordered", supplierOrder.prod.supplier_name); setSupplierOrder(null); }} data-testid="supplier-order-mark-sent">
                ✓ Ya envié el pedido al proveedor
              </Button>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Tip: Si el proveedor no tiene email/WhatsApp registrado, edita el producto en la pestana Productos y completa los campos &quot;Email proveedor&quot; y &quot;WhatsApp proveedor&quot;. Tambien puedes copiar el mensaje y pegarlo en el chat o formulario del proveedor.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
// ── Barra de scroll horizontal replicada arriba de una tabla ancha ──
// Uso: <StickyTopScrollbar targetRef={ref} /> antes del <div overflow-x-auto ref={ref}>.
// En viewport chico (<768px) se oculta para no duplicar UI en móvil.
function StickyTopScrollbar({ targetRef }) {
  const topRef = useRef(null);
  const innerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [innerWidth, setInnerWidth] = useState(0);

  // Efecto 1: mide el ancho del contenido para decidir si mostrar la barra.
  useEffect(() => {
    const wrapper = targetRef && targetRef.current;
    if (!wrapper) return;
    const findScroller = () => {
      const candidates = [wrapper, ...wrapper.querySelectorAll("div")];
      for (const c of candidates) {
        const style = window.getComputedStyle(c);
        if ((style.overflowX === "auto" || style.overflowX === "scroll") && c.scrollWidth > c.clientWidth) {
          return c;
        }
      }
      for (const c of candidates) {
        const style = window.getComputedStyle(c);
        if (style.overflowX === "auto" || style.overflowX === "scroll") return c;
      }
      return wrapper;
    };
    const sync = () => {
      const el = findScroller();
      const scrollW = el.scrollWidth;
      const clientW = el.clientWidth;
      setInnerWidth(scrollW);
      setVisible(scrollW > clientW + 2 && window.innerWidth >= 768);
    };
    sync();
    const t1 = setTimeout(sync, 100);
    const t2 = setTimeout(sync, 800);
    window.addEventListener("resize", sync);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(sync);
      ro.observe(wrapper);
    }
    return () => {
      window.removeEventListener("resize", sync);
      if (ro) ro.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [targetRef]);

  // Efecto 2: cuando la barra es visible y el DOM ya montó topRef.current,
  // enlazamos la sincronización bidireccional de scrollLeft. Se re-ejecuta
  // cada vez que `visible` cambia — arregla el bug donde el listener se
  // adjuntaba a topRef.current cuando aún era null.
  useEffect(() => {
    if (!visible) return;
    const wrapper = targetRef && targetRef.current;
    const topEl = topRef.current;
    if (!wrapper || !topEl) return;
    const findScroller = () => {
      const candidates = [wrapper, ...wrapper.querySelectorAll("div")];
      for (const c of candidates) {
        const style = window.getComputedStyle(c);
        if ((style.overflowX === "auto" || style.overflowX === "scroll") && c.scrollWidth > c.clientWidth) {
          return c;
        }
      }
      for (const c of candidates) {
        const style = window.getComputedStyle(c);
        if (style.overflowX === "auto" || style.overflowX === "scroll") return c;
      }
      return wrapper;
    };
    const el = findScroller();
    let syncing = false;
    const onTopScroll = () => {
      if (syncing) return;
      syncing = true;
      el.scrollLeft = topEl.scrollLeft;
      requestAnimationFrame(() => { syncing = false; });
    };
    const onTargetScroll = () => {
      if (syncing) return;
      syncing = true;
      topEl.scrollLeft = el.scrollLeft;
      requestAnimationFrame(() => { syncing = false; });
    };
    topEl.addEventListener("scroll", onTopScroll, { passive: true });
    el.addEventListener("scroll", onTargetScroll, { passive: true });
    // Sync inicial por si el target ya tenía scroll aplicado.
    topEl.scrollLeft = el.scrollLeft;
    return () => {
      topEl.removeEventListener("scroll", onTopScroll);
      el.removeEventListener("scroll", onTargetScroll);
    };
  }, [visible, innerWidth, targetRef]);

  if (!visible) return null;
  return (
    <div
      ref={topRef}
      className="overflow-x-auto overflow-y-hidden mb-1 sticky top-14 z-20 bg-[#0A0A0A] border-t border-white/5"
      style={{ height: 14 }}
      data-testid="reg-table-top-scrollbar"
    >
      <div ref={innerRef} style={{ width: innerWidth, height: 1 }} />
    </div>
  );
}


// ── Popover para revisar un adjunto (aprobar/rechazar/pendiente + nota) ──
function AttachmentReviewPopover({ regId, slotKey, att, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(att?.review_status || "pending");
  const [note, setNote] = useState(att?.review_note || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(att?.review_status || "pending");
      setNote(att?.review_note || "");
    }
  }, [open, att]);

  const filename = att?.filename || slotKey;
  const contentType = att?.content_type || "application/octet-stream";
  const currentStatus = att?.review_status || "pending";

  // Colores según estado
  const dot = currentStatus === "approved" ? "bg-green-400"
            : currentStatus === "rejected" ? "bg-red-400"
            : "bg-amber-300";
  const ring = currentStatus === "approved" ? "text-green-300 hover:bg-green-500/10"
             : currentStatus === "rejected" ? "text-red-300 hover:bg-red-500/10"
             : "text-blue-300 hover:bg-blue-500/10";

  const download = async () => {
    try {
      const safeName = filename.replace(/[^a-zA-Z0-9._-]+/g, "_");
      await downloadAuthenticated(`/event-registrations/${regId}/attachment/${slotKey}`, safeName, contentType);
    } catch {
      toast.error("Error descargando adjunto");
    }
  };

  const save = async (newStatus) => {
    setBusy(true);
    try {
      await apiPatch(`/event-registrations/${regId}/attachment/${slotKey}/review`, { status: newStatus, note });
      toast.success(newStatus === "approved" ? "Aprobado" : newStatus === "rejected" ? "Rechazado" : "Marcado pendiente");
      setStatus(newStatus);
      setOpen(false);
      if (onReviewed) onReviewed();
    } catch {
      toast.error("Error al guardar revisión");
    } finally { setBusy(false); }
  };

  const tip = `${slotKey}: ${filename}${att?.review_note ? ` — Nota: ${att.review_note}` : ""}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className={`h-6 w-6 relative ${ring}`}
          title={tip}
          data-testid={`reg-attachment-download-${regId}-${slotKey}`}
        >
          <Upload size={12} className="rotate-180" />
          <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${dot}`} aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="bg-[#0F0F0F] border-white/10 w-72 p-3" data-testid={`reg-attachment-review-${regId}-${slotKey}`}>
        <div className="mb-2">
          <div className="text-xs text-gray-500 uppercase tracking-widest">{slotKey}</div>
          <div className="text-sm text-white truncate" title={filename}>{filename}</div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full h-8 text-xs border-white/10 text-white hover:bg-white/5 mb-3"
          onClick={download}
          data-testid={`reg-attachment-open-${regId}-${slotKey}`}
        >
          <Upload size={12} className="rotate-180 mr-1" /> Descargar / Ver
        </Button>

        <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Estado</div>
        <div className="flex gap-1 mb-2">
          <Button
            size="sm"
            variant="outline"
            className={`flex-1 h-7 text-xs ${status === "approved" ? "bg-green-500/20 border-green-500 text-green-200" : "border-white/10 text-gray-300"}`}
            onClick={() => save("approved")}
            disabled={busy}
            data-testid={`reg-attachment-approve-${regId}-${slotKey}`}
          >
            ✓ Aprobar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={`flex-1 h-7 text-xs ${status === "rejected" ? "bg-red-500/20 border-red-500 text-red-200" : "border-white/10 text-gray-300"}`}
            onClick={() => save("rejected")}
            disabled={busy}
            data-testid={`reg-attachment-reject-${regId}-${slotKey}`}
          >
            ✕ Rechazar
          </Button>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className={`w-full h-7 text-[11px] ${status === "pending" ? "text-amber-300" : "text-gray-500"}`}
          onClick={() => save("pending")}
          disabled={busy}
          data-testid={`reg-attachment-pending-${regId}-${slotKey}`}
        >
          Marcar pendiente
        </Button>

        <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-2 mb-1">Nota (opcional)</div>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          placeholder="Ej: falta firma del médico, certificado vencido…"
          className="w-full bg-[#1A1A1A] border border-white/10 p-2 text-xs text-white focus:border-[#FF4D00] outline-none"
          data-testid={`reg-attachment-note-${regId}-${slotKey}`}
        />
        {att?.reviewed_at && (
          <div className="text-[10px] text-gray-500 mt-1">
            Revisado {String(att.reviewed_at).slice(0,16).replace("T"," ")} por {att.reviewed_by || "—"}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


function RegistrationsTab() {
  const [regs, setRegs] = useState([]);
  // filterEvent: array de event_ids seleccionados. [] = todos los eventos.
  const [filterEvent, setFilterEvent] = useState([]);
  const [filterDist, setFilterDist] = useState("all");
  const [filterGender, setFilterGender] = useState("all");
  const [filterCity, setFilterCity] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [selected, setSelected] = useState(new Set());
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashSelected, setTrashSelected] = useState(new Set());
  const tableScrollRef = useRef(null);
  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try { setTrash(await apiGet("/event-registrations/trash")); setTrashSelected(new Set()); }
    catch { toast.error("No se pudo cargar la papelera"); }
    finally { setTrashLoading(false); }
  }, []);
  const openTrash = async () => { await loadTrash(); setTrashOpen(true); };
  const restoreFromTrash = async (ids) => {
    if (!ids.length) return;
    try {
      const r = await apiPost("/event-registrations/bulk-restore", { ids });
      toast.success(`Restauradas: ${r.restored}`);
      await loadTrash();
      await load();
    } catch { toast.error("Error al restaurar"); }
  };
  const restoreAllByEvent = async (event_id, event_title) => {
    if (!window.confirm(`Restaurar TODAS las inscripciones en papelera del evento "${event_title}"?`)) return;
    try {
      const r = await apiPost("/event-registrations/bulk-restore", { event_id });
      toast.success(`Restauradas: ${r.restored}`);
      await loadTrash();
      await load();
    } catch { toast.error("Error al restaurar"); }
  };
  const purgeTrash = async () => {
    if (!window.confirm("VACIAR PAPELERA: se eliminarán DEFINITIVAMENTE. Esta acción no se puede deshacer. ¿Continuar?")) return;
    try {
      await apiDelete("/event-registrations/trash/purge");
      toast.success("Papelera vaciada");
      await loadTrash();
    } catch { toast.error("Error al vaciar"); }
  };
  const [eventsIndex, setEventsIndex] = useState({}); // event_id -> canonical title
  const load = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([apiGet("/event-registrations"), apiGet("/events").catch(() => [])]);
      setRegs(r);
      setSelected(new Set());
      const idx = {};
      (e || []).forEach(ev => { idx[ev.id] = ev.title; });
      setEventsIndex(idx);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Detección de duplicados por nombre / RUT / email / teléfono ──
  // Normalización robusta:
  //  - Nombre: tildes, minúsculas, puntuación, espacios múltiples → una forma
  //    "canónica" y una "sorted" (mismas palabras en orden alfabético) para
  //    detectar "Juan Perez" == "Perez Juan".
  //  - Email: minúsculas y trim.
  //  - Teléfono: sólo dígitos y últimos 8 (número chileno útil), evita fallar
  //    por "+56 9 1234 5678" vs "912345678".
  const stripDiacritics = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const normName = (s) => stripDiacritics(s).toLowerCase().replace(/[^a-z0-9\s]/g, " ").trim().replace(/\s+/g, " ");
  const normNameSorted = (s) => {
    const n = normName(s);
    if (!n) return "";
    return n.split(" ").filter(Boolean).sort().join(" ");
  };
  const normRut = (s) => (s || "").toString().replace(/[^0-9kK]/g, "").toLowerCase();
  const normEmail = (s) => (s || "").toString().trim().toLowerCase();
  const normPhone = (s) => {
    const digits = (s || "").toString().replace(/\D/g, "");
    return digits.length >= 8 ? digits.slice(-8) : ""; // últimos 8 dígitos
  };

  // Conteo por cada valor normalizado.
  // Bucket de duplicados:
  //   - Si el usuario tiene ≥2 eventos seleccionados en el multi-filtro → esos
  //     eventos se tratan como UN SOLO grupo (bucket "__combined__"): permite
  //     detectar duplicados entre listas cruzadas (caso: mismo evento dividido
  //     en 2 registros / cuenta y confirmación).
  //   - En cualquier otro caso (0 o 1 evento filtrado) → bucket = event_id
  //     (los duplicados siguen aislados por evento, evitando falsos positivos
  //     de una misma persona inscrita en 2 carreras distintas).
  //
  // En modo combinado sumamos email + teléfono al match (el usuario típicamente
  // reutiliza los mismos datos entre 2 formularios de la misma carrera).
  const selectedSet = new Set(filterEvent);
  const combinedMode = filterEvent.length >= 2;
  const dupBucket = (r) => (combinedMode && selectedSet.has(r.event_id)) ? "__combined__" : r.event_id;
  const nameCounts = {}, nameSortedCounts = {}, rutCounts = {}, emailCounts = {}, phoneCounts = {};
  regs.forEach(r => {
    const b = dupBucket(r);
    const n = normName(r.full_name); if (n) { const k = `${b}::${n}`; nameCounts[k] = (nameCounts[k] || 0) + 1; }
    const ns = normNameSorted(r.full_name); if (ns && ns !== normName(r.full_name)) { const k = `${b}::${ns}`; nameSortedCounts[k] = (nameSortedCounts[k] || 0) + 1; }
    const ru = normRut(r.rut); if (ru) { const k = `${b}::${ru}`; rutCounts[k] = (rutCounts[k] || 0) + 1; }
    if (combinedMode) {
      const em = normEmail(r.email); if (em) { const k = `${b}::${em}`; emailCounts[k] = (emailCounts[k] || 0) + 1; }
      const ph = normPhone(r.phone); if (ph) { const k = `${b}::${ph}`; phoneCounts[k] = (phoneCounts[k] || 0) + 1; }
    }
  });

  // Mapa id -> lista de razones por las que es duplicado (para highlight visual amber)
  const duplicateInfo = {};
  regs.forEach(r => {
    const reasons = [];
    const b = dupBucket(r);
    const n = normName(r.full_name);
    const ns = normNameSorted(r.full_name);
    const ru = normRut(r.rut);
    const nk = n ? `${b}::${n}` : "";
    const nsk = (ns && ns !== n) ? `${b}::${ns}` : "";
    const rk = ru ? `${b}::${ru}` : "";
    if (nk && nameCounts[nk] >= 2) reasons.push({ field: "nombre", count: nameCounts[nk] });
    else if (nsk && nameSortedCounts[nsk] >= 2) reasons.push({ field: "nombre", count: nameSortedCounts[nsk] });
    if (rk && rutCounts[rk] >= 2) reasons.push({ field: "RUT", count: rutCounts[rk] });
    if (combinedMode) {
      const em = normEmail(r.email);
      const ph = normPhone(r.phone);
      const ek = em ? `${b}::${em}` : "";
      const pk = ph ? `${b}::${ph}` : "";
      if (ek && emailCounts[ek] >= 2) reasons.push({ field: "email", count: emailCounts[ek] });
      if (pk && phoneCounts[pk] >= 2) reasons.push({ field: "teléfono", count: phoneCounts[pk] });
    }
    if (reasons.length) duplicateInfo[r.id] = reasons;
  });
  const duplicateCount = Object.keys(duplicateInfo).length;

  // ── Clusterización de duplicados con Union-Find ──
  // Agrupamos transitivamente por nombre y RUT (el email se excluye).
  // En cada cluster mantenemos la inscripción más antigua (por created_at)
  // como "keeper" y marcamos el resto como "extras" (borrables sin dejar a
  // la persona fuera del listado de inscritos).
  const parent = {};
  const findRoot = (x) => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };
  const unite = (a, b) => {
    const ra = findRoot(a), rb = findRoot(b);
    if (ra !== rb) parent[ra] = rb;
  };
  regs.forEach(r => { parent[r.id] = r.id; });
  const groupByKey = (getKey) => {
    const buckets = {};
    regs.forEach(r => {
      const k = getKey(r);
      if (!k) return;
      (buckets[k] = buckets[k] || []).push(r.id);
    });
    Object.values(buckets).forEach(ids => {
      if (ids.length < 2) return;
      for (let i = 1; i < ids.length; i++) unite(ids[0], ids[i]);
    });
  };
  // Aislar duplicados según el mismo bucket que arriba: entre eventos
  // seleccionados juntos comparten bucket, en otros casos se aislan por evento.
  // En modo combinado, agrupamos también por email + teléfono para capturar
  // coincidencias que no matchean por nombre/RUT (ej: agregó apellido, RUT vacío).
  groupByKey(r => { const n = normName(r.full_name); return n ? `${dupBucket(r)}::N::${n}` : ""; });
  groupByKey(r => { const ns = normNameSorted(r.full_name); const n = normName(r.full_name); return (ns && ns !== n) ? `${dupBucket(r)}::NS::${ns}` : ""; });
  groupByKey(r => { const u = normRut(r.rut); return u ? `${dupBucket(r)}::R::${u}` : ""; });
  if (combinedMode) {
    groupByKey(r => { const em = normEmail(r.email); return em ? `${dupBucket(r)}::E::${em}` : ""; });
    groupByKey(r => { const ph = normPhone(r.phone); return ph ? `${dupBucket(r)}::P::${ph}` : ""; });
  }

  const clusters = {};
  regs.forEach(r => {
    const root = findRoot(r.id);
    (clusters[root] = clusters[root] || []).push(r);
  });
  // duplicateExtras: ids que sobran (todos menos el más antiguo de cada cluster ≥2)
  const duplicateExtras = {};
  Object.values(clusters).forEach(list => {
    if (list.length < 2) return;
    const sorted = [...list].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    for (let i = 1; i < sorted.length; i++) duplicateExtras[sorted[i].id] = true;
  });
  const duplicateExtrasCount = Object.keys(duplicateExtras).length;

  let filtered = filterEvent.length === 0 ? regs : regs.filter(r => filterEvent.includes(r.event_id));
  if (filterDist !== "all") filtered = filtered.filter(r => r.distance_label === filterDist);
  if (filterGender !== "all") filtered = filtered.filter(r => r.gender === filterGender);
  if (filterCity !== "all") filtered = filtered.filter(r => (r.city || "").trim().toLowerCase() === filterCity);
  // Filtro por fecha de inscripción (created_at).
  if (dateFrom) filtered = filtered.filter(r => (r.created_at || "").slice(0, 10) >= dateFrom);
  if (dateTo) filtered = filtered.filter(r => (r.created_at || "").slice(0, 10) <= dateTo);
  if (showOnlyDuplicates) filtered = filtered.filter(r => duplicateExtras[r.id]);
  // Buscador libre por nombre o RUT (normalizado sin tildes/guiones/puntos).
  const q = searchQuery.trim();
  if (q) {
    const qName = normName(q);
    const qRut = normRut(q);
    filtered = filtered.filter(r => {
      const nameHit = qName && normName(r.full_name).includes(qName);
      const rutHit = qRut && normRut(r.rut).includes(qRut);
      return nameHit || rutHit;
    });
  }

  if (sortBy === "age") filtered = [...filtered].sort((a, b) => (a.age || 0) - (b.age || 0));
  else if (sortBy === "city") filtered = [...filtered].sort((a, b) => (a.city || "").localeCompare(b.city || ""));
  else if (sortBy === "name") filtered = [...filtered].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
  else if (sortBy === "email") {
    // Agrupa inscripciones que comparten email (mismo mail → filas contiguas),
    // luego ordena por fecha ascendente dentro de cada grupo.
    filtered = [...filtered].sort((a, b) => {
      const ea = (a.email || "").trim().toLowerCase();
      const eb = (b.email || "").trim().toLowerCase();
      if (ea !== eb) return ea.localeCompare(eb);
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
  }

  // uniqueEvents: usa titulo canonico del state 'events' si esta disponible,
  // sino cae al title cacheado en la inscripcion. Evita mostrar titulos legacy
  // cuando un evento fue renombrado despues de que sus inscripciones existieran.
  // uniqueEvents: enriquecido con:
  //  - `count`: cuántas inscripciones apuntan a ese event_id
  //  - `orphan`: true si el event_id ya no existe en la colección `events`
  //    (caso: evento renombrado/recreado — las inscripciones viejas quedan huérfanas)
  //  - `oldTitle`: si el título cacheado en la inscripción difiere del canónico
  //    del evento, lo mostramos para que el admin identifique la carrera.
  const _evCounts = {};
  const _evOldTitles = {};
  regs.forEach(r => {
    const id = r.event_id;
    _evCounts[id] = (_evCounts[id] || 0) + 1;
    const cached = (r.event_title || "").trim();
    const canonical = (eventsIndex[id] || "").trim();
    if (cached && canonical && cached !== canonical && !_evOldTitles[id]) _evOldTitles[id] = cached;
  });
  const uniqueEvents = [...new Map(regs.map(r => {
    const id = r.event_id;
    const canonical = eventsIndex[id];
    return [id, {
      id,
      title: canonical || r.event_title || "(sin título)",
      count: _evCounts[id] || 0,
      orphan: !canonical,
      oldTitle: _evOldTitles[id] || "",
    }];
  })).values()].sort((a, b) => (b.count || 0) - (a.count || 0));
  const uniqueDists = [...new Set(regs.map(r => r.distance_label))].filter(Boolean);
  // Ciudades únicas normalizadas (para el filtro), preservando el nombre original para mostrar.
  const cityMap = new Map();
  regs.forEach(r => {
    const raw = (r.city || "").trim();
    if (!raw) return;
    const key = raw.toLowerCase();
    if (!cityMap.has(key)) cityMap.set(key, raw);
  });
  const uniqueCities = [...cityMap.entries()].sort((a, b) => a[1].localeCompare(b[1]));

  const hasActiveFilters = () => (
    filterEvent.length > 0 || filterDist !== "all" || filterGender !== "all" ||
    filterCity !== "all" || showOnlyDuplicates || !!dateFrom || !!dateTo || !!searchQuery.trim()
  );

  const handleDelete = async (id) => {
    if (!window.confirm("Enviar esta inscripción a la papelera? (podrás restaurarla)")) return;
    try { await apiDelete(`/event-registrations/${id}`); toast.success("Enviada a la papelera"); load(); } catch { toast.error("Error"); }
  };

  const handlePrint = () => { window.print(); };

  // Columnas: clave -> {label, getter}. Usado para encabezado, tabla y copy-column.
  const columns = [
    { key: "full_name", label: "Nombre",     get: r => r.full_name || "" },
    { key: "email",     label: "Email",      get: r => r.email || "" },
    { key: "phone",     label: "Celular",    get: r => r.phone || "" },
    { key: "rut",       label: "RUT",        get: r => r.rut || "" },
    { key: "age",       label: "Edad",       get: r => (r.age ?? "") + "" },
    { key: "gender",    label: "Sexo",       get: r => r.gender || "" },
    { key: "event",     label: "Evento",     get: r => r.event_title || "" },
    { key: "distance",  label: "Distancia",  get: r => r.distance_label || "" },
    { key: "category",  label: "Categoria",  get: r => r.category || "" },
    { key: "shirt",     label: "Talla",      get: r => r.shirt_size || "" },
    { key: "city",      label: "Ciudad",     get: r => r.city || "" },
    { key: "club",      label: "Club",       get: r => r.club || "" },
  ];

  const copyColumn = async (col) => {
    const values = filtered.map(col.get).filter(v => v !== "");
    if (values.length === 0) { toast.error(`Sin datos en "${col.label}"`); return; }
    const text = values.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${values.length} ${col.label.toLowerCase()} copiados al portapapeles`);
    } catch {
      // Fallback para navegadores que no soportan Clipboard API
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success(`${values.length} ${col.label.toLowerCase()} copiados`); }
      catch { toast.error("No se pudo copiar"); }
      document.body.removeChild(ta);
    }
  };

  // ── Selección masiva ──
  const filteredIds = filtered.map(r => r.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));
  const someFilteredSelected = !allFilteredSelected && filteredIds.some(id => selected.has(id));

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredIds.forEach(id => next.delete(id));
      else filteredIds.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const exportSelectedXlsx = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(`${API}/event-registrations/export-xlsx-selected`, { ids }, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = "inscripciones_seleccionadas.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${ids.length} inscripciones exportadas`);
    } catch { toast.error("Error al exportar"); }
  };

  // Descarga autenticada de archivos (CSV / XLSX completo).
  // Usar <a href> directo no envía el token Bearer y resulta en archivos dañados (HTML de error con extensión .xlsx).
  const exportAllCsv = () => downloadAuthenticated("/event-registrations/export", "inscripciones_provertical.csv", "text/csv; charset=utf-8");
  const exportAllXlsx = async () => {
    // Si hay filtros, usar endpoint "selected" con los ids filtrados — incluye filtro de fechas.
    if (hasActiveFilters()) {
      const ids = filtered.map(r => r.id);
      if (ids.length === 0) { toast.error("No hay inscripciones que coincidan con los filtros"); return; }
      try {
        const res = await axios.post(`${API}/event-registrations/export-xlsx-selected`, { ids }, { headers: getHeaders(), responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
        const a = document.createElement("a");
        a.href = url; a.download = `inscripciones_filtradas_${ids.length}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`${ids.length} inscripciones exportadas (filtradas)`);
      } catch (e) {
        toast.error(e?.response?.status === 401 ? "Sesión expirada. Vuelve a iniciar sesión." : "Error al exportar");
      }
      return;
    }
    downloadAuthenticated("/event-registrations/export-xlsx", "inscripciones_provertical.xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Enviar ${ids.length} inscripción(es) a la papelera? Podrás restaurarlas después.`)) return;
    try {
      const data = await apiPost("/event-registrations/bulk-delete", { ids });
      toast.success(`${data.deleted} enviada(s) a papelera`);
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Inscripciones ({filtered.length})</h3>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="text-gray-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o RUT..."
              className="bg-[#1A1A1A] border border-white/10 pl-7 pr-7 h-8 text-xs text-gray-200 outline-none w-56 focus:border-[#FF4D00]/60"
              data-testid="reg-filter-search"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                data-testid="reg-filter-search-clear"
                aria-label="Limpiar búsqueda"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="bg-[#1A1A1A] border border-white/10 hover:border-white/20 h-8 px-3 text-xs text-gray-200 flex items-center gap-2 min-w-[11rem] max-w-[16rem] justify-between"
                data-testid="reg-filter-event-trigger"
                title={filterEvent.length === 0 ? "Todos los eventos" : filterEvent.map(id => eventsIndex[id] || uniqueEvents.find(e => e.id === id)?.title || id).join(", ")}
              >
                <span className="truncate">
                  {filterEvent.length === 0
                    ? "Todos los eventos"
                    : filterEvent.length === 1
                      ? (eventsIndex[filterEvent[0]] || uniqueEvents.find(e => e.id === filterEvent[0])?.title || "1 evento")
                      : `${filterEvent.length} eventos`}
                </span>
                <span className="text-gray-500 shrink-0">▾</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0 bg-[#0F0F0F] border-white/10" align="start">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Filtrar por evento ({uniqueEvents.length})</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFilterEvent(uniqueEvents.map(e => e.id))}
                    className="text-[10px] uppercase tracking-wider text-blue-300 hover:text-blue-200"
                    data-testid="reg-filter-event-select-all"
                    title="Seleccionar todos los eventos (útil si un evento fue renombrado y hay inscripciones huérfanas)"
                  >
                    Todos
                  </button>
                  {filterEvent.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFilterEvent([])}
                      className="text-[10px] uppercase tracking-wider text-[#FF4D00] hover:text-orange-400"
                      data-testid="reg-filter-event-clear"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto py-1">
                {uniqueEvents.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-500">Sin eventos</div>
                )}
                {uniqueEvents.map(ev => {
                  const checked = filterEvent.includes(ev.id);
                  return (
                    <label
                      key={ev.id}
                      className={`flex items-start gap-2 px-3 py-2 text-xs text-gray-200 hover:bg-white/5 cursor-pointer border-l-2 ${ev.orphan ? "border-amber-500/60" : "border-transparent"}`}
                      data-testid={`reg-filter-event-option-${ev.id}`}
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={checked}
                        onCheckedChange={(v) => {
                          setFilterEvent(prev => v ? [...prev, ev.id] : prev.filter(x => x !== ev.id));
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 justify-between">
                          <span className="truncate font-medium">{ev.title}</span>
                          <span className="text-[10px] font-mono text-gray-400 shrink-0 bg-white/5 px-1.5 py-0.5">{ev.count}</span>
                        </div>
                        {ev.orphan && (
                          <p className="text-[10px] text-amber-400 mt-0.5" title={`event_id=${ev.id} — evento eliminado o inexistente`}>
                            ⚠ Evento borrado — inscripciones huérfanas
                          </p>
                        )}
                        {ev.oldTitle && (
                          <p className="text-[10px] text-gray-500 mt-0.5 truncate" title={ev.oldTitle}>
                            antes: {ev.oldTitle}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          <Select value={filterDist} onValueChange={setFilterDist}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-28 h-8 text-xs"><SelectValue placeholder="Distancia" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{uniqueDists.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterGender} onValueChange={setFilterGender}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-32 h-8 text-xs"><SelectValue placeholder="Sexo" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="masculino">Masculino</SelectItem><SelectItem value="femenino">Femenino</SelectItem><SelectItem value="otro">Otro</SelectItem></SelectContent>
          </Select>
          <Select value={filterCity} onValueChange={setFilterCity}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-36 h-8 text-xs" data-testid="reg-filter-city"><SelectValue placeholder="Ciudad" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Todas las ciudades</SelectItem>
              {uniqueCities.map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="bg-[#1A1A1A] border-white/10 w-32 h-8 text-xs"><SelectValue placeholder="Ordenar" /></SelectTrigger>
            <SelectContent><SelectItem value="date">Fecha</SelectItem><SelectItem value="name">Nombre</SelectItem><SelectItem value="age">Edad</SelectItem><SelectItem value="city">Ciudad</SelectItem><SelectItem value="email">Email (agrupar familias)</SelectItem></SelectContent>
          </Select>
          {/* Filtro de rango de fechas (fecha de inscripcion). Aplica a la vista y al export. */}
          <div className="flex items-center gap-1 bg-[#1A1A1A] border border-white/10 px-2 h-8 text-xs">
            <Calendar size={12} className="text-gray-500" />
            <span className="text-[10px] uppercase text-gray-500 tracking-wider">Desde</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-transparent text-xs text-gray-200 outline-none w-32" data-testid="reg-filter-date-from" />
          </div>
          <div className="flex items-center gap-1 bg-[#1A1A1A] border border-white/10 px-2 h-8 text-xs">
            <span className="text-[10px] uppercase text-gray-500 tracking-wider">Hasta</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-transparent text-xs text-gray-200 outline-none w-32" data-testid="reg-filter-date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <Button size="sm" variant="ghost" className="h-8 text-xs text-gray-400 hover:text-white" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="reg-filter-date-clear">
              <X size={12} className="mr-1" /> Fechas
            </Button>
          )}
          <Button onClick={exportAllCsv} size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-8 text-xs" data-testid="admin-export-registrations">
            <DownloadSimple size={14} className="mr-1" /> CSV
          </Button>
          <Button onClick={exportAllXlsx} size="sm" variant="outline" className="border-green-500/40 text-green-400 h-8 text-xs" data-testid="admin-export-registrations-xlsx">
            <FileXls size={14} className="mr-1" /> Excel{hasActiveFilters() ? ` (${filtered.length})` : ""}
          </Button>
          <Button
            onClick={() => setShowOnlyDuplicates(v => !v)}
            size="sm"
            variant="outline"
            className={`h-8 text-xs ${showOnlyDuplicates ? "bg-amber-500/20 border-amber-500 text-amber-300" : duplicateExtrasCount > 0 ? "border-amber-500/40 text-amber-400" : "border-white/10 text-gray-500"}`}
            data-testid="reg-filter-duplicates"
            title={duplicateExtrasCount === 0 ? "No hay inscripciones duplicadas" : `${duplicateExtrasCount} inscripción(es) duplicada(s) borrable(s)${combinedMode ? " — comparando los eventos seleccionados como una sola lista" : " (se conserva la más antigua de cada grupo)"}`}
          >
            🔁 Duplicados{combinedMode ? " combinados" : ""}{duplicateExtrasCount > 0 ? ` (${duplicateExtrasCount})` : ""}{showOnlyDuplicates ? " · Activo" : ""}
          </Button>
          <Button
            onClick={openTrash}
            size="sm"
            variant="outline"
            className="h-8 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10"
            data-testid="reg-open-trash"
            title="Ver papelera (inscripciones borradas — puedes restaurar)"
          >
            <Trash size={12} className="mr-1" /> Papelera
          </Button>
          {filterEvent.length === 1 && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
              data-testid="reg-send-consent-links"
              title="Enviar link de firma de consentimiento a los inscritos del evento seleccionado que aún no han firmado"
              onClick={async () => {
                if (!window.confirm("Enviar link de firma por email a los inscritos del evento seleccionado sin firmar?")) return;
                try {
                  const r = await apiPost(`/events/${filterEvent[0]}/send-consent-links`, {});
                  toast.success(`${r.sent} email(s) enviado(s) de ${r.total} pendiente(s)`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Error al enviar");
                }
              }}
            >
              <PaperPlaneTilt size={12} className="mr-1" /> Enviar consentimientos
            </Button>
          )}
          {filterEvent.length === 1 && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
              data-testid="reg-send-reminders"
              title="Enviar recordatorio manual del evento a TODOS los inscritos (independiente de fecha)"
              onClick={async () => {
                if (!window.confirm("Enviar recordatorio por email a TODOS los inscritos del evento seleccionado?")) return;
                try {
                  const r = await apiPost(`/events/${filterEvent[0]}/send-reminders`, {});
                  toast.success(`${r.sent} enviados de ${r.total} inscritos${r.failed ? ` (${r.failed} fallaron)` : ""}`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Error al enviar recordatorios");
                }
              }}
            >
              <PaperPlaneTilt size={12} className="mr-1" /> Recordatorio manual
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-white/20 text-gray-300 h-8 text-xs" onClick={handlePrint}>
            <DownloadSimple size={14} className="mr-1" /> PDF/Imprimir
          </Button>
        </div>
      </div>
      {/* Barra de scroll horizontal siempre visible en la parte superior de la tabla (útil en PC). */}
      <StickyTopScrollbar targetRef={tableScrollRef} />
      <div ref={tableScrollRef}>
        {selected.size > 0 && (
          <div className="flex items-center gap-3 bg-[#1A1A1A] border border-[#FF4D00]/40 px-4 py-2 mb-3 text-xs" data-testid="bulk-actions-bar">
            <span className="text-[#FF4D00] font-bold uppercase tracking-wider">{selected.size} seleccionada(s)</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" className="border-green-500/40 text-green-400 h-8" onClick={exportSelectedXlsx} data-testid="bulk-export-xlsx">
              <FileXls size={14} className="mr-1" /> Exportar selección a Excel
            </Button>
            <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 h-8" onClick={bulkDelete} data-testid="bulk-delete">
              <Trash size={14} className="mr-1" /> Eliminar seleccionadas
            </Button>
            <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8" onClick={clearSelection}>
              <X size={14} className="mr-1" /> Limpiar
            </Button>
          </div>
        )}
        <Table className="min-w-[1600px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleAllVisible}
                  data-testid="select-all-rows"
                  className="border-white/40"
                />
              </TableHead>
              {columns.map(col => (
                <TableHead key={col.key}>
                  <div className="inline-flex items-center gap-1 group">
                    <span>{col.label}</span>
                    <button
                      onClick={() => copyColumn(col)}
                      title={`Copiar todos los valores de "${col.label}"`}
                      className="opacity-40 group-hover:opacity-100 hover:text-[#FF4D00] transition-opacity"
                      data-testid={`copy-col-${col.key}`}
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </TableHead>
              ))}
              <TableHead title="Consentimiento firmado">Consent.</TableHead>
              <TableHead title="Documentos adjuntos">Docs</TableHead>
              <TableHead>Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => {
              const dup = duplicateInfo[r.id];
              const dupTitle = dup ? dup.map(d => `${d.field} repetido (${d.count} veces)`).join(" · ") : "";
              return (
              <TableRow
                key={r.id}
                className={`${selected.has(r.id) ? "bg-[#FF4D00]/5" : dup ? "bg-amber-500/10 hover:bg-amber-500/15" : ""} ${dup ? "border-l-4 border-l-amber-500" : ""}`}
                data-testid={dup ? `reg-row-duplicate-${r.id}` : `reg-row-${r.id}`}
              >
                <TableCell>
                  <Checkbox
                    checked={selected.has(r.id)}
                    onCheckedChange={() => toggleRow(r.id)}
                    data-testid={`select-row-${r.id}`}
                    className="border-white/40"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <p className={`font-medium text-sm ${dup?.some(d => d.field === "nombre") ? "text-amber-300" : ""}`}>{r.full_name}</p>
                    {dup && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1.5 py-0.5"
                        title={dupTitle}
                        data-testid={`dup-badge-${r.id}`}
                      >
                        🔁 {dup.map(d => d.field[0].toUpperCase()).join("")}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className={`text-xs ${dup?.some(d => d.field === "email") ? "text-amber-300" : "text-gray-400"}`}>{r.email}</TableCell>
                <TableCell className="text-xs text-gray-400">{r.phone}</TableCell>
                <TableCell className={`text-xs ${dup?.some(d => d.field === "RUT") ? "text-amber-300" : "text-gray-400"}`}>{r.rut}</TableCell>
                <TableCell className="text-xs">{r.age}</TableCell>
                <TableCell className="text-xs text-gray-400">{r.gender}</TableCell>
                <TableCell className="text-xs">{r.event_title}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs text-[#FF4D00] border-[#FF4D00]/30">{r.distance_label}</Badge></TableCell>
                <TableCell className="text-xs text-gray-400">{r.category}</TableCell>
                <TableCell className="text-xs">{r.shirt_size}</TableCell>
                <TableCell className="text-xs text-gray-400">{r.city}</TableCell>
                <TableCell className="text-xs text-gray-400">{r.club || "-"}</TableCell>
                <TableCell>
                  {r.consent_signed_at ? (
                    <div className="flex items-center gap-1">
                      <span title={`Firmado ${(r.consent_signed_at || "").slice(0,16).replace("T"," ")}`} className="text-green-400 text-lg leading-none" data-testid={`reg-consent-signed-${r.id}`}>✓</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-gray-400 hover:text-white"
                        onClick={() => downloadAuthenticated(`/event-registrations/${r.id}/consent-pdf`, `consentimiento_${(r.full_name || 'firma').replace(/\s+/g,'_')}.pdf`, 'application/pdf')}
                        data-testid={`reg-consent-download-${r.id}`}
                        title="Descargar PDF del consentimiento"
                      >
                        <DownloadSimple size={12} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-blue-300 hover:text-blue-100"
                        onClick={async () => {
                          if (!window.confirm(`Reenviar consentimiento firmado a ${r.email}?`)) return;
                          try {
                            const res = await apiPost(`/event-registrations/${r.id}/resend-consent-email`, {});
                            toast.success(`Reenviado a ${res.to}`);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Error al reenviar");
                          }
                        }}
                        data-testid={`reg-consent-resend-${r.id}`}
                        title={`Reenviar por email a ${r.email}`}
                      >
                        <PaperPlaneTilt size={12} />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-gray-600 text-xs" title="Sin firmar">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {(() => {
                    const att = r.attachments || {};
                    const keys = Object.keys(att).filter(k => att[k] && att[k].content);
                    if (keys.length === 0) return <span className="text-gray-600 text-xs">—</span>;
                    return (
                      <div className="flex gap-0.5 flex-wrap" data-testid={`reg-attachments-${r.id}`}>
                        {keys.map(k => (
                          <AttachmentReviewPopover
                            key={k}
                            regId={r.id}
                            slotKey={k}
                            att={att[k]}
                            onReviewed={load}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell><Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDelete(r.id)}><Trash size={14} /></Button></TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {filtered.length === 0 && <p className="text-center text-gray-400 py-8">No hay inscripciones</p>}

      {/* ── Dialog de Papelera ── */}
      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent className="bg-[#121212] border-red-500/20 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="trash-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash size={18} className="text-red-400" />
              Papelera de Inscripciones ({trash.length})
            </DialogTitle>
            <DialogDescription>
              Las inscripciones borradas se conservan aquí. Puedes restaurarlas o vaciar la papelera definitivamente.
            </DialogDescription>
          </DialogHeader>

          {trashLoading ? (
            <p className="text-center text-gray-400 py-8">Cargando...</p>
          ) : trash.length === 0 ? (
            <p className="text-center text-gray-400 py-8">La papelera está vacía.</p>
          ) : (
            <>
              {/* Grupos por evento para restauración masiva */}
              {(() => {
                const byEvent = {};
                trash.forEach(r => {
                  const k = r.event_id || "?";
                  if (!byEvent[k]) byEvent[k] = { event_id: r.event_id, event_title: r.event_title, count: 0 };
                  byEvent[k].count += 1;
                });
                const groups = Object.values(byEvent).sort((a, b) => b.count - a.count);
                return (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {groups.map(g => (
                      <Button
                        key={g.event_id}
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs border-green-500/30 text-green-300"
                        onClick={() => restoreAllByEvent(g.event_id, g.event_title)}
                        data-testid={`trash-restore-event-${g.event_id}`}
                        title={`Restaurar las ${g.count} inscripciones del evento ${g.event_title}`}
                      >
                        ↺ Restaurar {g.event_title} ({g.count})
                      </Button>
                    ))}
                  </div>
                );
              })()}

              <div className="flex items-center gap-2 mb-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-green-500/40 text-green-400 h-8 text-xs"
                  disabled={trashSelected.size === 0}
                  onClick={() => restoreFromTrash([...trashSelected])}
                  data-testid="trash-restore-selected"
                >
                  ↺ Restaurar seleccionadas ({trashSelected.size})
                </Button>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/40 text-red-400 h-8 text-xs"
                  onClick={purgeTrash}
                  data-testid="trash-purge"
                >
                  <Trash size={12} className="mr-1" /> Vaciar papelera (definitivo)
                </Button>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={trashSelected.size === trash.length && trash.length > 0}
                          onCheckedChange={(v) => setTrashSelected(v ? new Set(trash.map(r => r.id)) : new Set())}
                          data-testid="trash-select-all"
                          className="border-white/40"
                        />
                      </TableHead>
                      <TableHead className="text-xs">Nombre</TableHead>
                      <TableHead className="text-xs">Email</TableHead>
                      <TableHead className="text-xs">Evento</TableHead>
                      <TableHead className="text-xs">Distancia</TableHead>
                      <TableHead className="text-xs">Borrada</TableHead>
                      <TableHead className="text-xs">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trash.map(r => (
                      <TableRow key={r.id} data-testid={`trash-row-${r.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={trashSelected.has(r.id)}
                            onCheckedChange={(v) => {
                              const next = new Set(trashSelected);
                              if (v) next.add(r.id); else next.delete(r.id);
                              setTrashSelected(next);
                            }}
                            className="border-white/40"
                          />
                        </TableCell>
                        <TableCell className="text-xs">{r.full_name}</TableCell>
                        <TableCell className="text-xs text-gray-400">{r.email}</TableCell>
                        <TableCell className="text-xs">{r.event_title}</TableCell>
                        <TableCell className="text-xs"><Badge variant="outline" className="text-xs text-[#FF4D00] border-[#FF4D00]/30">{r.distance_label}</Badge></TableCell>
                        <TableCell className="text-[10px] text-gray-500">{(r.deleted_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="text-green-400 h-7 text-xs" onClick={() => restoreFromTrash([r.id])} data-testid={`trash-restore-${r.id}`}>
                            ↺ Restaurar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Contact Tab ──
function ContactTab() {
  const [form, setForm] = useState({ phone: "", email: "", address: "", city: "", region: "", whatsapp: "", instagram: "", facebook: "", tiktok: "", youtube: "", website: "", schedule: "", extra_info: "", map_embed_url: "" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    apiGet("/contact-info").then(data => { if (data) setForm(prev => ({ ...prev, ...data })); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    try { await apiPut("/contact-info", form); toast.success("Informacion actualizada"); } catch { toast.error("Error"); }
  };

  if (!loaded) return <p className="text-gray-400">Cargando...</p>;

  return (
    <div>
      <h3 className="text-lg font-semibold font-['Cabinet_Grotesk'] mb-4">Informacion de Contacto</h3>

      {/* Basic Contact */}
      <div className="mb-6">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-3">Datos Principales</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          <div><Label className="text-xs text-gray-400">Telefono</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+56 9 1234 5678" data-testid="contact-form-phone" /></div>
          <div><Label className="text-xs text-gray-400">Email</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="contacto@provertical.cl" data-testid="contact-form-email" /></div>
          <div><Label className="text-xs text-gray-400">WhatsApp</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.whatsapp} onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value }))} placeholder="+56912345678" /></div>
          <div><Label className="text-xs text-gray-400">Sitio Web</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://provertical.cl" /></div>
        </div>
      </div>

      {/* Location */}
      <div className="mb-6">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-3">Ubicacion</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          <div className="md:col-span-2"><Label className="text-xs text-gray-400">Direccion</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Calle Principal 123" /></div>
          <div><Label className="text-xs text-gray-400">Ciudad</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Coyhaique" /></div>
          <div><Label className="text-xs text-gray-400">Region</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.region} onChange={e => setForm(p => ({ ...p, region: e.target.value }))} placeholder="Aysen, Chile" /></div>
          <div className="md:col-span-2"><Label className="text-xs text-gray-400">URL Mapa Embebido (Google Maps embed)</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.map_embed_url} onChange={e => setForm(p => ({ ...p, map_embed_url: e.target.value }))} placeholder="https://www.google.com/maps/embed?..." /></div>
        </div>
      </div>

      {/* Social Media */}
      <div className="mb-6">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-3">Redes Sociales</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          <div><Label className="text-xs text-gray-400">Instagram</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.instagram} onChange={e => setForm(p => ({ ...p, instagram: e.target.value }))} placeholder="@provertical" /></div>
          <div><Label className="text-xs text-gray-400">Facebook</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.facebook} onChange={e => setForm(p => ({ ...p, facebook: e.target.value }))} placeholder="provertical" /></div>
          <div><Label className="text-xs text-gray-400">TikTok</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.tiktok} onChange={e => setForm(p => ({ ...p, tiktok: e.target.value }))} placeholder="@provertical" /></div>
          <div><Label className="text-xs text-gray-400">YouTube</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.youtube} onChange={e => setForm(p => ({ ...p, youtube: e.target.value }))} placeholder="ProVertical" /></div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mb-6">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500 mb-3">Informacion Adicional</p>
        <div className="max-w-3xl space-y-4">
          <div><Label className="text-xs text-gray-400">Horario de Atencion</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.schedule} onChange={e => setForm(p => ({ ...p, schedule: e.target.value }))} placeholder="Lunes a Viernes 9:00 - 18:00" /></div>
          <div><Label className="text-xs text-gray-400">Informacion Extra (se mostrara en la pagina de contacto)</Label>
            <Textarea className="bg-[#1A1A1A] border-white/10 mt-1 min-h-[80px]" value={form.extra_info} onChange={e => setForm(p => ({ ...p, extra_info: e.target.value }))} placeholder="Agrega cualquier informacion adicional que quieras mostrar a los visitantes: metodos de pago, politicas, puntos de retiro, etc." /></div>
        </div>
      </div>

      <Button className="bg-[#FF4D00] hover:bg-[#E64500] font-bold" onClick={handleSave} data-testid="contact-form-save">Guardar Cambios</Button>
    </div>
  );
}

// ── Gallery Tab ──
function GalleryTab() {
  const [albums, setAlbums] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", event_name: "", date: "", cover_image: "", photos: [] });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => { try { setAlbums(await apiGet("/gallery")); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ title: "", event_name: "", date: "", cover_image: "", photos: [] });
  const openCreate = () => { resetForm(); setEditing(null); setOpen(true); };
  const openEdit = (album) => {
    setForm({ title: album.title, event_name: album.event_name || "", date: album.date || "", cover_image: album.cover_image || "", photos: album.photos || [] });
    setEditing(album.id); setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) { await apiPut(`/gallery/${editing}`, form); toast.success("Album actualizado"); }
      else { await apiPost("/gallery", form); toast.success("Album creado"); }
      setOpen(false); load();
    } catch { toast.error("Error"); }
  };
  const handleDelete = async (id) => { if (!window.confirm("Eliminar album?")) return; try { await apiDelete(`/gallery/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); } };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      // Galería: mantener mejor calidad y resolución que el resto del sitio
      // (las fotos de galería suelen tener más protagonismo visual).
      const compressed = await compressImage(file, { maxWidth: 2200, maxHeight: 2200, quality: 0.9, maxSizeMB: 3 });
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      setForm(p => ({ ...p, photos: [...p.photos, { url: data.url, caption: "" }] }));
      toast.success(`Foto subida (${formatBytes(file.size)} → ${formatBytes(compressed.size)})`);
    } catch { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  const addPhotoUrl = () => setForm(p => ({ ...p, photos: [...p.photos, { url: "", caption: "" }] }));
  const removePhoto = (idx) => setForm(p => ({ ...p, photos: p.photos.filter((_, i) => i !== idx) }));
  const updatePhoto = (idx, field, value) => setForm(p => ({ ...p, photos: p.photos.map((ph, i) => i === idx ? { ...ph, [field]: value } : ph) }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Galeria ({albums.length} albums)</h3>
        <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500]" onClick={openCreate} data-testid="admin-add-album"><Plus size={14} className="mr-1" />Nuevo Album</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Album</TableHead><TableHead>Evento</TableHead><TableHead>Fecha</TableHead><TableHead>Fotos</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {albums.map(a => (
            <TableRow key={a.id}>
              <TableCell className="font-medium">{a.title}</TableCell>
              <TableCell className="text-gray-400">{a.event_name || "-"}</TableCell>
              <TableCell className="text-gray-400">{a.date || "-"}</TableCell>
              <TableCell><Badge variant="outline" className="text-xs">{a.photos?.length || 0}</Badge></TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(a)}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDelete(a.id)}><Trash size={14} /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {albums.length === 0 && <p className="text-center text-gray-400 py-8">No hay albums</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Album" : "Nuevo Album"}</DialogTitle><DialogDescription>Gestiona las fotos del album</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Titulo del Album</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="album-form-title" /></div>
              <div><Label className="text-xs text-gray-400">Nombre del Evento</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.event_name} onChange={e => setForm(p => ({ ...p, event_name: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Fecha</Label><Input type="date" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
              <div><Label className="text-xs text-gray-400">Imagen de Portada (URL)</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.cover_image} onChange={e => setForm(p => ({ ...p, cover_image: e.target.value }))} /></div>
            </div>

            <div className="pt-3 border-t border-white/10">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-sm font-semibold text-white">Fotos ({form.photos.length})</Label>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" multiple className="hidden" onChange={e => { [...e.target.files].forEach(f => uploadPhoto(f)); }} />
                    <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs pointer-events-none" disabled={uploading}>
                      <Upload size={12} className="mr-1" /> {uploading ? "Subiendo..." : "Subir Fotos"}
                    </Button>
                  </label>
                  <Button size="sm" variant="outline" className="border-white/20 text-gray-300 h-7 text-xs" onClick={addPhotoUrl}>
                    <Plus size={12} className="mr-1" /> URL
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                {form.photos.map((photo, idx) => (
                  <div key={idx} className="relative bg-[#1A1A1A] border border-white/5 p-1">
                    {(photo.url || "").startsWith("/uploads") || (photo.url || "").startsWith("http") ? (
                      <img src={photo.url?.startsWith("/uploads") ? `${process.env.REACT_APP_BACKEND_URL}${photo.url}` : photo.url} alt="" className="w-full h-20 object-cover" />
                    ) : (
                      <Input className="bg-[#121212] border-white/10 h-8 text-xs" value={photo.url} placeholder="URL de imagen" onChange={e => updatePhoto(idx, "url", e.target.value)} />
                    )}
                    <Input className="bg-[#121212] border-white/10 h-6 text-xs mt-1" value={photo.caption || ""} placeholder="Descripcion" onChange={e => updatePhoto(idx, "caption", e.target.value)} />
                    <button className="absolute top-0 right-0 bg-red-500/80 text-white p-0.5" onClick={() => removePhoto(idx)}><X size={10} /></button>
                  </div>
                ))}
              </div>
            </div>
            <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] font-bold mt-2" onClick={handleSave} data-testid="album-form-save">Guardar Album</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── About Tab ──
function AboutTab() {
  const [form, setForm] = useState({ history_title: "", history_content: "", history_photos: [], coaches: [], athletes: [] });
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { apiGet("/about").then(d => { setForm(d); setLoaded(true); }).catch(() => setLoaded(true)); }, []);

  const handleSave = async () => { try { await apiPut("/about", form); toast.success("Actualizado"); } catch { toast.error("Error"); } };

  const uploadImg = async (file, callback) => {
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      callback(data.url); toast.success(`Imagen subida (${formatBytes(file.size)} → ${formatBytes(compressed.size)})`);
    } catch { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  if (!loaded) return <p className="text-gray-400">Cargando...</p>;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Pagina "Que es Pro Vertical?"</h3>

      {/* History */}
      <div>
        <Label className="text-sm font-semibold text-white">Historia</Label>
        <Input className="bg-[#1A1A1A] border-white/10 mt-2" value={form.history_title} onChange={e => setForm(p => ({ ...p, history_title: e.target.value }))} placeholder="Titulo" />
        <Textarea className="bg-[#1A1A1A] border-white/10 mt-2 min-h-[100px]" value={form.history_content} onChange={e => setForm(p => ({ ...p, history_content: e.target.value }))} placeholder="Cuenta la historia de Pro Vertical..." />
        <div className="flex gap-2 mt-2 items-center">
          <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImg(e.target.files[0], (url) => setForm(p => ({ ...p, history_photos: [...p.history_photos, url] })))} />
            <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs pointer-events-none" disabled={uploading}><Upload size={12} className="mr-1" /> Foto</Button>
          </label>
          <span className="text-xs text-gray-500">{form.history_photos?.length || 0} fotos</span>
          {form.history_photos?.length > 0 && <Button size="sm" variant="ghost" className="text-red-400 h-7 text-xs" onClick={() => setForm(p => ({ ...p, history_photos: p.history_photos.slice(0, -1) }))}>Quitar ultima</Button>}
        </div>
      </div>

      {/* Coaches */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <Label className="text-sm font-semibold text-white">Entrenadores</Label>
          <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs" onClick={() => setForm(p => ({ ...p, coaches: [...p.coaches, { name: "", role: "", bio: "", credentials: "", photo: "" }] }))}>
            <Plus size={12} className="mr-1" /> Agregar
          </Button>
        </div>
        {form.coaches?.map((coach, idx) => (
          <div key={idx} className="bg-[#1A1A1A] border border-white/5 p-3 mb-2">
            <div className="flex gap-2 mb-2">
              <Input className="bg-[#121212] border-white/10 h-8 text-sm flex-1" value={coach.name} placeholder="Nombre" onChange={e => { const c = [...form.coaches]; c[idx] = { ...c[idx], name: e.target.value }; setForm(p => ({ ...p, coaches: c })); }} />
              <Input className="bg-[#121212] border-white/10 h-8 text-sm flex-1" value={coach.role} placeholder="Rol (ej: Head Coach)" onChange={e => { const c = [...form.coaches]; c[idx] = { ...c[idx], role: e.target.value }; setForm(p => ({ ...p, coaches: c })); }} />
              <Button size="icon" variant="ghost" className="text-red-400 h-8 w-8" onClick={() => setForm(p => ({ ...p, coaches: p.coaches.filter((_, i) => i !== idx) }))}><Trash size={14} /></Button>
            </div>
            <Textarea className="bg-[#121212] border-white/10 text-sm min-h-[40px] mb-2" value={coach.bio} placeholder="Biografia breve" onChange={e => { const c = [...form.coaches]; c[idx] = { ...c[idx], bio: e.target.value }; setForm(p => ({ ...p, coaches: c })); }} />
            <Input className="bg-[#121212] border-white/10 h-8 text-sm mb-2" value={coach.credentials} placeholder="Curriculum/Credenciales" onChange={e => { const c = [...form.coaches]; c[idx] = { ...c[idx], credentials: e.target.value }; setForm(p => ({ ...p, coaches: c })); }} />
            <div className="flex gap-2 items-center">
              <Input className="bg-[#121212] border-white/10 h-8 text-sm flex-1" value={coach.photo} placeholder="URL foto" onChange={e => { const c = [...form.coaches]; c[idx] = { ...c[idx], photo: e.target.value }; setForm(p => ({ ...p, coaches: c })); }} />
              <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImg(e.target.files[0], (url) => { const c = [...form.coaches]; c[idx] = { ...c[idx], photo: url }; setForm(p => ({ ...p, coaches: c })); })} />
                <Button size="sm" variant="outline" className="h-7 text-xs border-white/20 pointer-events-none"><Upload size={12} /></Button>
              </label>
            </div>
          </div>
        ))}
      </div>

      {/* Athletes */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <Label className="text-sm font-semibold text-white">Deportistas y Logros</Label>
          <Button size="sm" variant="outline" className="border-[#FF4D00]/30 text-[#FF4D00] h-7 text-xs" onClick={() => setForm(p => ({ ...p, athletes: [...p.athletes, { name: "", achievement: "", photo: "" }] }))}>
            <Plus size={12} className="mr-1" /> Agregar
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {form.athletes?.map((ath, idx) => (
            <div key={idx} className="bg-[#1A1A1A] border border-white/5 p-2">
              <div className="flex gap-2 mb-1">
                <Input className="bg-[#121212] border-white/10 h-7 text-xs flex-1" value={ath.name} placeholder="Nombre" onChange={e => { const a = [...form.athletes]; a[idx] = { ...a[idx], name: e.target.value }; setForm(p => ({ ...p, athletes: a })); }} />
                <Button size="icon" variant="ghost" className="text-red-400 h-7 w-7" onClick={() => setForm(p => ({ ...p, athletes: p.athletes.filter((_, i) => i !== idx) }))}><Trash size={12} /></Button>
              </div>
              <Input className="bg-[#121212] border-white/10 h-7 text-xs mb-1" value={ath.achievement} placeholder="Logro" onChange={e => { const a = [...form.athletes]; a[idx] = { ...a[idx], achievement: e.target.value }; setForm(p => ({ ...p, athletes: a })); }} />
              <div className="flex gap-1 items-center">
                <Input className="bg-[#121212] border-white/10 h-7 text-xs flex-1" value={ath.photo} placeholder="URL foto" onChange={e => { const a = [...form.athletes]; a[idx] = { ...a[idx], photo: e.target.value }; setForm(p => ({ ...p, athletes: a })); }} />
                <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImg(e.target.files[0], (url) => { const a = [...form.athletes]; a[idx] = { ...a[idx], photo: url }; setForm(p => ({ ...p, athletes: a })); })} />
                  <Button size="icon" variant="ghost" className="h-7 w-7 pointer-events-none"><Upload size={12} /></Button>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button className="bg-[#FF4D00] hover:bg-[#E64500] font-bold" onClick={handleSave} data-testid="about-save-btn">Guardar Cambios</Button>
    </div>
  );
}

// ── Discounts Tab ──
function DiscountsTab() {
  const [codes, setCodes] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", discount_type: "percentage", discount_value: "10", max_uses: "100", applies_to: "all", applies_to_id: "", applies_to_name: "" });

  const load = useCallback(async () => {
    try {
      const [c, e, p] = await Promise.all([apiGet("/discount-codes"), apiGet("/events"), apiGet("/products")]);
      setCodes(c); setEvents(e); setProducts(p);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error("Ingresa un codigo"); return; }
    try {
      await apiPost("/discount-codes", form);
      toast.success("Codigo creado");
      setOpen(false); setForm({ code: "", discount_type: "percentage", discount_value: "10", max_uses: "100", applies_to: "all", applies_to_id: "", applies_to_name: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (code) => {
    try { await apiPut(`/discount-codes/${code.id}`, { active: !code.active }); toast.success(code.active ? "Desactivado" : "Activado"); load(); } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => { if (!window.confirm("Eliminar codigo?")) return; try { await apiDelete(`/discount-codes/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); } };

  const appliesItems = [
    { value: "all", label: "Todos los items" },
    ...events.map(e => ({ value: `event:${e.id}`, label: `Evento: ${e.title}` })),
    ...products.map(p => ({ value: `product:${p.id}`, label: `Producto: ${p.name}` })),
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Codigos de Descuento ({codes.length})</h3>
        <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500]" onClick={() => setOpen(true)} data-testid="admin-add-discount"><Plus size={14} className="mr-1" />Nuevo Codigo</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Codigo</TableHead><TableHead>Descuento</TableHead><TableHead>Usos</TableHead><TableHead>Aplica a</TableHead><TableHead>Estado</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {codes.map(c => (
            <TableRow key={c.id}>
              <TableCell className="font-mono font-bold text-[#FF4D00]">{c.code}</TableCell>
              <TableCell>{c.discount_value}{c.discount_type === "percentage" ? "%" : " CLP"}</TableCell>
              <TableCell className="text-gray-400">{c.used_count}/{c.max_uses}</TableCell>
              <TableCell className="text-xs text-gray-400">{c.applies_to === "all" ? "Todos" : c.applies_to_name || c.applies_to}</TableCell>
              <TableCell><Badge variant="outline" className={c.active ? "text-green-400 border-green-400/30" : "text-red-400 border-red-400/30"}>{c.active ? "Activo" : "Inactivo"}</Badge></TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className={c.active ? "text-red-400" : "text-green-400"} onClick={() => toggleActive(c)}>{c.active ? "Desactivar" : "Activar"}</Button>
                  <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDelete(c.id)}><Trash size={14} /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {codes.length === 0 && <p className="text-center text-gray-400 py-8">No hay codigos de descuento</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Nuevo Codigo de Descuento</DialogTitle><DialogDescription>Crea un codigo para descuentos en eventos o productos</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-400">Codigo (ej: TRAIL20)</Label>
              <Input className="bg-[#1A1A1A] border-white/10 mt-1 uppercase font-mono" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="TRAIL20" data-testid="discount-form-code" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-400">Tipo</Label>
                <Select value={form.discount_type} onValueChange={v => setForm(p => ({ ...p, discount_type: v }))}>
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">Porcentaje (%)</SelectItem><SelectItem value="fixed">Monto fijo (CLP)</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs text-gray-400">Valor</Label>
                <Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.discount_value} onChange={e => setForm(p => ({ ...p, discount_value: e.target.value }))} data-testid="discount-form-value" /></div>
            </div>
            <div><Label className="text-xs text-gray-400">Maximo de usos</Label>
              <Input type="number" className="bg-[#1A1A1A] border-white/10 mt-1" value={form.max_uses} onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))} data-testid="discount-form-maxuses" /></div>
            <div><Label className="text-xs text-gray-400">Aplica a</Label>
              <Select value={form.applies_to === "all" ? "all" : `${form.applies_to}:${form.applies_to_id}`} onValueChange={v => {
                if (v === "all") { setForm(p => ({ ...p, applies_to: "all", applies_to_id: "", applies_to_name: "" })); }
                else { const [type, id] = v.split(":"); const name = appliesItems.find(i => i.value === v)?.label || ""; setForm(p => ({ ...p, applies_to: type, applies_to_id: id, applies_to_name: name })); }
              }}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{appliesItems.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] font-bold mt-2" onClick={handleSave} data-testid="discount-form-save">Crear Codigo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Landing / Home Tab ──
// ─── BLOQUE MANTENIMIENTO ───
function RecompressBlock() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRecompress = async () => {
    if (!window.confirm("Esto recomprimira TODAS las imagenes ya subidas (productos, eventos, planes, galeria, etc). El proceso puede tardar 30-90 segundos. Continuar?")) return;
    setRunning(true);
    setResult(null);
    try {
      const data = await apiPost("/admin/recompress-images", {});
      setResult(data);
      toast.success(`OK · ${data.images_compressed} imagenes comprimidas (${data.savings_mb} MB ahorrados)`);
    } catch { toast.error("Error al recomprimir"); }
    finally { setRunning(false); }
  };

  return (
    <div className="p-4 border border-yellow-500/30 bg-yellow-500/5" data-testid="recompress-block">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-yellow-300 mb-1">Mantenimiento — Rendimiento</p>
          <p className="text-sm text-gray-200 font-semibold">Recomprimir imagenes ya subidas</p>
          <p className="text-xs text-gray-400 mt-1 max-w-2xl">Las imagenes nuevas ya se comprimen al subirlas. Si tienes imagenes antiguas pesadas, este boton las redimensiona a 1280px y re-encoda a JPEG 78% para ganar fluidez en mobile. Es seguro correrlo cuantas veces quieras.</p>
        </div>
        <Button onClick={handleRecompress} disabled={running} className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold" data-testid="recompress-btn">
          {running ? "Procesando..." : "Recomprimir todo"}
        </Button>
      </div>
      {result && (
        <div className="mt-3 bg-[#0F0F0F] border border-white/5 p-3 text-xs space-y-1" data-testid="recompress-result">
          <p className="text-emerald-300 font-semibold">✓ Listo. {result.images_compressed} de {result.images_processed} imagenes comprimidas.</p>
          <p className="text-gray-400">Ahorro total: <span className="text-white font-bold">{result.savings_mb} MB</span> · Docs actualizados: {result.docs_updated}</p>
          <details className="mt-1 cursor-pointer">
            <summary className="text-gray-500">Detalle por coleccion</summary>
            <ul className="mt-1 ml-3 space-y-0.5 text-gray-400">
              {Object.entries(result.by_collection).map(([k, v]) => (
                <li key={k}>· <span className="text-gray-300">{k}</span>: {v.compressed}/{v.images} comprimidas</li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
}


function LandingTab() {
  const initialForm = { hero_image_url: "", hero_location: "", hero_title1: "", hero_title2: "", hero_desc: "", hero_cta_store_label: "", hero_cta_events_label: "", hero_cta_training_label: "", show_cta_store: true, show_cta_events: true, show_cta_training: true, cta_title: "", cta_desc: "", cta_contact_label: "", cta_whatsapp_url: "", feat_events_image_url: "", feat_training_image_url: "", feat_store_image_url: "" };
  const [form, setForm] = useState(initialForm);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingField, setUploadingField] = useState("");

  useEffect(() => {
    apiGet("/landing").then(data => {
      setForm(prev => ({ ...prev, ...(data || {}) }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    try { await apiPut("/landing", form); toast.success("Inicio actualizado"); } catch { toast.error("Error"); }
  };

  const uploadToField = async (file, field) => {
    if (!file) return;
    setUploading(true);
    setUploadingField(field);
    const compressed = await compressImage(file);
    const fd = new FormData(); fd.append("file", compressed);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${data.url}` : data.url;
      setForm(p => ({ ...p, [field]: url }));
      toast.success(`Imagen subida (${formatBytes(file.size)} → ${formatBytes(compressed.size)})`);
    } catch { toast.error("Error subiendo imagen"); }
    finally { setUploading(false); setUploadingField(""); }
  };

  const handleUpload = (file) => uploadToField(file, "hero_image_url");

  if (!loaded) return <p className="text-gray-400">Cargando...</p>;

  return (
    <div className="space-y-6">
      <RecompressBlock />
      <div>
        <h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">Pantalla de Inicio</h3>
        <p className="text-xs text-gray-500 mt-1">Modifica los textos, botones e imagen de la pantalla principal. Los campos vacios usan los valores por defecto en cada idioma.</p>
      </div>

      {/* HERO */}
      <div className="space-y-3 max-w-3xl">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Cabecera Principal (Hero)</p>
        <div>
          <Label className="text-xs text-gray-400">Imagen de fondo</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input className="bg-[#1A1A1A] border-white/10 flex-1" value={form.hero_image_url} onChange={e => setForm(p => ({ ...p, hero_image_url: e.target.value }))} placeholder="https://... o vacio para usar la imagen por defecto" data-testid="landing-hero-image" />
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { handleUpload(e.target.files[0]); e.target.value = ""; }} />
              <span className={`inline-flex items-center gap-1 text-xs px-3 py-2 border ${uploading ? "border-white/10 text-gray-600" : "border-white/20 text-gray-300 hover:border-[#FF4D00] hover:text-[#FF4D00]"}`}><Upload size={12} /> {uploading ? "Subiendo..." : "Subir"}</span>
            </label>
          </div>
          {form.hero_image_url && <div className="mt-2 w-full max-w-md aspect-video bg-[#1A1A1A] border border-white/5 overflow-hidden"><img src={form.hero_image_url} alt="" className="w-full h-full object-cover" /></div>}
        </div>
        <div><Label className="text-xs text-gray-400">Ubicacion (texto pequeno arriba)</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.hero_location} onChange={e => setForm(p => ({ ...p, hero_location: e.target.value }))} placeholder="Coyhaique, Aysen — Patagonia Chile" data-testid="landing-hero-location" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-400">Titulo linea 1</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.hero_title1} onChange={e => setForm(p => ({ ...p, hero_title1: e.target.value }))} placeholder="Trail Running" data-testid="landing-hero-title1" /></div>
          <div><Label className="text-xs text-gray-400">Titulo linea 2 (en naranja)</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.hero_title2} onChange={e => setForm(p => ({ ...p, hero_title2: e.target.value }))} placeholder="Pro Vertical" data-testid="landing-hero-title2" /></div>
        </div>
        <div><Label className="text-xs text-gray-400">Descripcion</Label><Textarea className="bg-[#1A1A1A] border-white/10 mt-1 min-h-[80px]" value={form.hero_desc} onChange={e => setForm(p => ({ ...p, hero_desc: e.target.value }))} placeholder="Descripcion corta debajo del titulo" data-testid="landing-hero-desc" /></div>
      </div>

      {/* CTAs */}
      <div className="space-y-3 max-w-3xl">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Botones del Hero</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2"><input type="checkbox" checked={form.show_cta_store} onChange={e => setForm(p => ({ ...p, show_cta_store: e.target.checked }))} className="accent-[#FF4D00]" data-testid="landing-show-store" /><Label className="text-xs text-gray-300">Mostrar boton Tienda</Label></div>
            <Input className="bg-[#1A1A1A] border-white/10" value={form.hero_cta_store_label} onChange={e => setForm(p => ({ ...p, hero_cta_store_label: e.target.value }))} placeholder="Explorar Tienda" disabled={!form.show_cta_store} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><input type="checkbox" checked={form.show_cta_events} onChange={e => setForm(p => ({ ...p, show_cta_events: e.target.checked }))} className="accent-[#FF4D00]" data-testid="landing-show-events" /><Label className="text-xs text-gray-300">Mostrar boton Eventos</Label></div>
            <Input className="bg-[#1A1A1A] border-white/10" value={form.hero_cta_events_label} onChange={e => setForm(p => ({ ...p, hero_cta_events_label: e.target.value }))} placeholder="Ver Eventos" disabled={!form.show_cta_events} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><input type="checkbox" checked={form.show_cta_training} onChange={e => setForm(p => ({ ...p, show_cta_training: e.target.checked }))} className="accent-[#FF4D00]" data-testid="landing-show-training" /><Label className="text-xs text-gray-300">Mostrar boton Entrenamientos</Label></div>
            <Input className="bg-[#1A1A1A] border-white/10" value={form.hero_cta_training_label} onChange={e => setForm(p => ({ ...p, hero_cta_training_label: e.target.value }))} placeholder="Entrenamientos personalizados" disabled={!form.show_cta_training} />
          </div>
        </div>
      </div>

      {/* IMAGENES TARJETAS DESTACADAS DE LA PORTADA */}
      <div className="space-y-3 max-w-3xl">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Imagenes de las tarjetas (portada)</p>
        <p className="text-[11px] text-gray-500 -mt-1">Imagen mostrada en cada tarjeta destacada del inicio. Deja vacio para usar la imagen por defecto.</p>
        {[
          { key: "feat_events_image_url", label: "Tarjeta Eventos Deportivos", testid: "landing-feat-events" },
          { key: "feat_training_image_url", label: "Tarjeta Entrenamientos", testid: "landing-feat-training" },
          { key: "feat_store_image_url", label: "Tarjeta Tienda", testid: "landing-feat-store" },
        ].map(({ key, label, testid }) => (
          <div key={key}>
            <Label className="text-xs text-gray-400">{label}</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input className="bg-[#1A1A1A] border-white/10 flex-1" value={form[key] || ""} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} placeholder="https://... o vacio para usar la imagen por defecto" data-testid={`${testid}-input`} />
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadToField(e.target.files[0], key); e.target.value = ""; }} data-testid={`${testid}-upload`} />
                <span className={`inline-flex items-center gap-1 text-xs px-3 py-2 border ${uploading && uploadingField === key ? "border-white/10 text-gray-600" : "border-white/20 text-gray-300 hover:border-[#FF4D00] hover:text-[#FF4D00]"}`}><Upload size={12} /> {uploading && uploadingField === key ? "Subiendo..." : "Subir"}</span>
              </label>
              {form[key] && (
                <button type="button" onClick={() => setForm(p => ({ ...p, [key]: "" }))} className="text-xs px-2 py-2 border border-white/15 text-gray-400 hover:text-red-400 hover:border-red-400/40" data-testid={`${testid}-clear`}>
                  <X size={12} />
                </button>
              )}
            </div>
            {form[key] && <div className="mt-2 w-full max-w-md aspect-[16/9] bg-[#1A1A1A] border border-white/5 overflow-hidden"><img src={form[key]} alt="" className="w-full h-full object-cover" /></div>}
          </div>
        ))}
      </div>

      {/* CTA SECTION (bottom) */}
      <div className="space-y-3 max-w-3xl">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gray-500">Seccion CTA (al final del inicio)</p>
        <div><Label className="text-xs text-gray-400">Titulo</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.cta_title} onChange={e => setForm(p => ({ ...p, cta_title: e.target.value }))} placeholder="Listo para conquistar la cordillera?" data-testid="landing-cta-title" /></div>
        <div><Label className="text-xs text-gray-400">Descripcion</Label><Textarea className="bg-[#1A1A1A] border-white/10 mt-1 min-h-[60px]" value={form.cta_desc} onChange={e => setForm(p => ({ ...p, cta_desc: e.target.value }))} placeholder="Texto motivador..." data-testid="landing-cta-desc" /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-400">Texto boton Contacto</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.cta_contact_label} onChange={e => setForm(p => ({ ...p, cta_contact_label: e.target.value }))} placeholder="Contactanos" /></div>
          <div><Label className="text-xs text-gray-400">URL WhatsApp boton</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={form.cta_whatsapp_url} onChange={e => setForm(p => ({ ...p, cta_whatsapp_url: e.target.value }))} placeholder="https://wa.me/56912345678" /></div>
        </div>
      </div>

      <Button className="bg-[#FF4D00] hover:bg-[#E64500] font-bold" onClick={handleSave} data-testid="landing-save">Guardar Cambios</Button>
    </div>
  );
}

// ── Main Admin Dashboard ──
export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [focusMode, setFocusMode] = useState({ active: false, until: "" });
  const [focusMenuOpen, setFocusMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  // Poll notifications + focus mode
  useEffect(() => {
    if (!user) return;
    const fetchAll = () => {
      apiGet("/notifications").then(setNotifications).catch(() => {});
      apiGet("/admin/focus-mode").then(setFocusMode).catch(() => {});
    };
    fetchAll();
    const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const markAllRead = () => { apiPost("/notifications/read-all", {}).then(() => setNotifications(n => n.map(x => ({ ...x, read: true })))).catch(() => {}); };

  // Conteo de notificaciones por tipo (de las últimas 50, no leídas + leídas recientes)
  const TYPE_META = {
    registration: { label: "Inscripciones", icon: Calendar, color: "text-[#FF4D00]" },
    payment: { label: "Pagos", icon: ShoppingBag, color: "text-emerald-400" },
    order: { label: "Pedidos", icon: Package, color: "text-blue-400" },
    training_consultation: { label: "Consultas", icon: ChatCircle, color: "text-purple-400" },
    plan_expiring: { label: "Planes por vencer", icon: Barbell, color: "text-amber-400" },
  };
  // Contar últimas 24 horas por tipo
  const cutoff24h = Date.now() - 24 * 3600 * 1000;
  const countsByType = notifications.reduce((acc, n) => {
    const ts = new Date(n.created_at).getTime();
    if (ts < cutoff24h) return acc;
    const t = n.type || "other";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  // Tiempo restante de Modo Concentración
  const focusRemaining = (() => {
    if (!focusMode.active || !focusMode.until) return "";
    const ms = new Date(focusMode.until).getTime() - Date.now();
    if (ms <= 0) return "";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })();

  const activateFocus = async (hours, labelEs) => {
    try {
      const { data } = await axios.post(`${API}/admin/focus-mode`, { duration_hours: hours }, { headers: getHeaders() });
      setFocusMode(data);
      setFocusMenuOpen(false);
      toast.success(`Modo concentración activo · ${labelEs}`);
    } catch { toast.error("Error"); }
  };
  const clearFocus = async () => {
    try {
      await axios.delete(`${API}/admin/focus-mode`, { headers: getHeaders() });
      setFocusMode({ active: false, until: "" });
      setFocusMenuOpen(false);
      toast.success("Modo concentración desactivado");
    } catch { toast.error("Error"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Cargando...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Admin Header */}
      <div className="bg-[#0a0a0a] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mountains size={24} weight="bold" className="text-[#FF4D00]" />
            <span className="font-['Cabinet_Grotesk'] font-black text-lg uppercase tracking-tight">Pro Vertical</span>
            <Badge variant="outline" className="text-xs text-[#FF4D00] border-[#FF4D00]/30 ml-2">Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            {/* Modo Concentración */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className={focusMode.active ? "text-amber-400 hover:text-amber-300" : "text-gray-400 hover:text-white"}
                onClick={() => setFocusMenuOpen(!focusMenuOpen)}
                data-testid="admin-focus-mode-btn"
                title={focusMode.active ? `Modo concentración activo · ${focusRemaining} restantes` : "Activar modo concentración"}
              >
                {focusMode.active ? <BellSlash size={18} /> : <Bell size={18} weight="duotone" className="opacity-60" />}
                {focusMode.active && <span className="ml-1 text-[11px] font-semibold">{focusRemaining}</span>}
              </Button>
              {focusMenuOpen && (
                <div className="absolute right-0 top-10 w-64 bg-[#121212] border border-white/10 shadow-xl z-50" data-testid="focus-mode-menu">
                  <div className="p-3 border-b border-white/10 flex justify-between items-center">
                    <div>
                      <p className="text-sm font-bold">Modo Concentración</p>
                      <p className="text-[11px] text-gray-500">Silencia la campanita temporalmente</p>
                    </div>
                    <button onClick={() => setFocusMenuOpen(false)} className="text-gray-400"><X size={14} /></button>
                  </div>
                  {focusMode.active ? (
                    <div className="p-3">
                      <div className="bg-amber-500/10 border border-amber-500/30 p-2 mb-2 text-xs text-amber-300">
                        <BellSlash size={14} className="inline mr-1" /> Activo · {focusRemaining} restantes
                      </div>
                      <button onClick={clearFocus} className="w-full text-xs px-3 py-2 bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold" data-testid="focus-mode-clear">
                        Desactivar ahora
                      </button>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {[
                        { h: 1, label: "1 hora" },
                        { h: 2, label: "2 horas" },
                        { h: 4, label: "4 horas" },
                        { h: 8, label: "8 horas" },
                        { h: 24, label: "Hasta mañana (24h)" },
                      ].map(opt => (
                        <button
                          key={opt.h}
                          onClick={() => activateFocus(opt.h, opt.label)}
                          className="w-full text-left text-xs px-3 py-2 hover:bg-white/5 border border-transparent hover:border-white/10"
                          data-testid={`focus-mode-${opt.h}h`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Notifications Bell */}
            <div className="relative">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white relative" onClick={() => setShowNotifs(!showNotifs)} data-testid="admin-notifications-btn">
                <Bell size={18} />
                {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-[#FF4D00] text-white text-xs rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-bold">{unreadCount}</span>}
              </Button>
              {showNotifs && (
                <div className="absolute right-0 top-10 w-80 bg-[#121212] border border-white/10 shadow-xl z-50 max-h-[480px] overflow-y-auto" data-testid="notifications-dropdown">
                  <div className="p-3 border-b border-white/10 flex justify-between items-center sticky top-0 bg-[#121212] z-10">
                    <span className="text-sm font-bold">Notificaciones</span>
                    <div className="flex items-center gap-1">
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-[10px] uppercase tracking-wider text-[#FF4D00] hover:text-[#FF4D00]/80 font-bold" data-testid="mark-all-read-btn">
                          Marcar leídas
                        </button>
                      )}
                      <button onClick={() => setShowNotifs(false)} className="text-gray-400"><X size={14} /></button>
                    </div>
                  </div>
                  {/* Chips de conteo por tipo (últimas 24h) */}
                  {Object.keys(countsByType).length > 0 && (
                    <div className="px-3 py-2 border-b border-white/10" data-testid="notification-type-chips">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Últimas 24h</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(countsByType).map(([type, count]) => {
                          const meta = TYPE_META[type] || { label: type, icon: Bell, color: "text-gray-400" };
                          const Icon = meta.icon;
                          return (
                            <span key={type} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-white/5 border border-white/10 ${meta.color}`} data-testid={`notif-chip-${type}`}>
                              <Icon size={12} /> {count} {meta.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {focusMode.active && (
                    <div className="px-3 py-2 bg-amber-500/10 border-b border-amber-500/20 text-[11px] text-amber-300 flex items-center gap-1">
                      <BellSlash size={12} /> Modo concentración activo · nuevas notificaciones quedarán silenciadas ({focusRemaining})
                    </div>
                  )}
                  {notifications.length === 0 ? <p className="p-4 text-xs text-gray-500 text-center">Sin notificaciones</p> : (
                    notifications.slice(0, 20).map(n => (
                      <div key={n.id} className={`p-3 border-b border-white/5 ${!n.read ? "bg-[#FF4D00]/5" : ""}`}>
                        <p className="text-sm font-medium">{n.title}</p>
                        <p className="text-xs text-gray-400">{n.message}</p>
                        <p className="text-xs text-gray-600 mt-1">{new Date(n.created_at).toLocaleString("es-CL")}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <span className="text-sm text-gray-400 hidden sm:inline">{user.email}</span>
            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-btn">
              <SignOut size={16} className="mr-1" /> {t("admin_logout")}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="landing">
          <TabsList className="bg-[#121212] border border-white/10 mb-6 flex-wrap h-auto p-1">
            <TabsTrigger value="landing" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-landing">
              <House size={14} /> Inicio
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-events">
              <Calendar size={14} /> {t("admin_events")}
            </TabsTrigger>
            <TabsTrigger value="training" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-training">
              <Barbell size={14} /> {t("admin_training")}
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-products">
              <ShoppingBag size={14} /> {t("admin_products")}
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-orders">
              <Package size={14} /> {t("admin_orders")}
            </TabsTrigger>
            <TabsTrigger value="registrations" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-registrations">
              <ClipboardText size={14} /> Inscripciones
            </TabsTrigger>
            <TabsTrigger value="consultations" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-consultations">
              <ChatCircle size={14} /> Consultas
            </TabsTrigger>
            <TabsTrigger value="client-plans" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-client-plans">
              <Barbell size={14} /> Clientes Activos
            </TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-contact">
              <AddressBook size={14} /> {t("admin_contact")}
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-gallery">
              <Images size={14} /> Galeria
            </TabsTrigger>
            <TabsTrigger value="about" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-about">
              <Info size={14} /> Pro Vertical
            </TabsTrigger>
            <TabsTrigger value="discounts" className="data-[state=active]:bg-[#FF4D00] data-[state=active]:text-white gap-1" data-testid="admin-tab-discounts">
              <ShoppingBag size={14} /> Descuentos
            </TabsTrigger>
          </TabsList>

          <div className="bg-[#0a0a0a] border border-white/10 p-6">
            <TabsContent value="landing"><LandingTab /></TabsContent>
            <TabsContent value="events"><EventsTab /></TabsContent>
            <TabsContent value="training"><TrainingTab /></TabsContent>
            <TabsContent value="products"><ProductsTab /></TabsContent>
            <TabsContent value="orders"><OrdersTab /></TabsContent>
            <TabsContent value="registrations"><RegistrationsTab /></TabsContent>
            <TabsContent value="consultations"><ConsultationsTab /></TabsContent>
            <TabsContent value="client-plans"><ClientPlansTab /></TabsContent>
            <TabsContent value="contact"><ContactTab /></TabsContent>
            <TabsContent value="gallery"><GalleryTab /></TabsContent>
            <TabsContent value="about"><AboutTab /></TabsContent>
            <TabsContent value="discounts"><DiscountsTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}


// ─── CONSULTAS DE ENTRENAMIENTO ───
function ConsultationsTab() {
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    try { setItems(await apiGet("/training-consultations")); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = filterStatus === "all" ? items : items.filter(c => (c.status || "new") === filterStatus);
  const counts = {
    all: items.length,
    new: items.filter(c => (c.status || "new") === "new").length,
    responded: items.filter(c => c.status === "responded").length,
  };

  const markStatus = async (id, status) => {
    try {
      await fetch(`${API}/training-consultations/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status })
      });
      toast.success(status === "responded" ? "Marcada como respondida" : "Marcada como nueva");
      load();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar consulta?")) return;
    try { await apiDelete(`/training-consultations/${id}`); toast.success("Eliminada"); load(); } catch { toast.error("Error"); }
  };

  const waLink = (c) => {
    const phone = String(c.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
    const msg = `Hola ${c.full_name || ""}, te respondo tu consulta sobre el plan "${c.plan_title || ""}": `;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold font-['Cabinet_Grotesk']">Consultas de Entrenamiento</h2>
          <p className="text-xs text-gray-400">{items.length} total · {counts.new} sin responder</p>
        </div>
        <div className="flex gap-2">
          {[
            { v: "all", label: "Todas", c: counts.all },
            { v: "new", label: "Sin responder", c: counts.new },
            { v: "responded", label: "Respondidas", c: counts.responded },
          ].map(f => (
            <button
              key={f.v}
              onClick={() => setFilterStatus(f.v)}
              className={`px-3 h-8 text-[11px] uppercase tracking-wider font-bold border ${filterStatus === f.v ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/15 text-gray-300 hover:border-white/40"}`}
              data-testid={`consult-filter-${f.v}`}
            >{f.label} ({f.c})</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500 border border-white/5 bg-[#0F0F0F]">
          <ChatCircle size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">{filterStatus === "all" ? "Sin consultas aun." : "Sin consultas en este estado."}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="consultations-list">
          {filtered.map(c => {
            const isNew = (c.status || "new") === "new";
            return (
              <div key={c.id} className={`bg-[#121212] border p-4 ${isNew ? "border-[#FF4D00]/40" : "border-white/5"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${isNew ? "bg-[#FF4D00] text-white" : "bg-white/10 text-gray-400"}`}>
                        {isNew ? "Nueva" : "Respondida"}
                      </span>
                      <span className="text-xs text-gray-500">{new Date(c.created_at).toLocaleString("es-CL")}</span>
                      {c.plan_title && <span className="text-xs text-gray-400">· Plan: <span className="text-white">{c.plan_title}</span></span>}
                    </div>
                    <p className="text-base font-semibold">{c.full_name}</p>
                    <p className="text-xs text-gray-400">{c.phone}</p>
                    <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{c.message}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <a href={waLink(c)} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="bg-green-600 hover:bg-green-500 text-white w-full" data-testid={`consult-wa-${c.id}`}>
                        <WhatsappLogo size={14} className="mr-1" weight="bold" /> WhatsApp
                      </Button>
                    </a>
                    {isNew ? (
                      <Button size="sm" variant="outline" className="border-white/15 text-gray-300 hover:text-white" onClick={() => markStatus(c.id, "responded")} data-testid={`consult-mark-responded-${c.id}`}>
                        Marcar respondida
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="border-white/15 text-gray-300 hover:text-white" onClick={() => markStatus(c.id, "new")}>
                        Marcar nueva
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => handleDelete(c.id)} data-testid={`consult-delete-${c.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── CLIENTES ACTIVOS (PLANES + NOTIF. VENCIMIENTO) ───
function ClientPlansTab() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [running, setRunning] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const emptyForm = {
    client_name: "", client_email: "", client_phone: "",
    plan_id: "", duration_weeks: 0,
    start_date: new Date().toISOString().slice(0, 10),
    notes: "", status: "active",
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const [cps, ps] = await Promise.all([apiGet("/client-plans"), apiGet("/training-plans")]);
      setItems(cps); setPlans(ps);
    } catch { toast.error("Error cargando clientes"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onOpenNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const onOpenEdit = (cp) => {
    setEditing(cp);
    setForm({
      client_name: cp.client_name || "",
      client_email: cp.client_email || "",
      client_phone: cp.client_phone || "",
      plan_id: cp.plan_id || "",
      duration_weeks: cp.duration_weeks || 0,
      start_date: (cp.start_date || "").slice(0, 10),
      notes: cp.notes || "",
      status: cp.status || "active",
    });
    setDialogOpen(true);
  };

  const onSave = async () => {
    if (!form.client_name.trim() || !form.plan_id || !form.start_date) {
      toast.error("Cliente, plan y fecha de inicio son obligatorios");
      return;
    }
    try {
      const selectedPlan = plans.find(p => p.id === form.plan_id);
      const payload = {
        ...form,
        duration_weeks: Number(form.duration_weeks) || (selectedPlan?.duration_weeks || 8),
        plan_title: selectedPlan?.title || "",
      };
      if (editing) {
        await apiPut(`/client-plans/${editing.id}`, payload);
        toast.success("Cliente actualizado");
      } else {
        await apiPost("/client-plans", payload);
        toast.success("Cliente agregado");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    try { await apiDelete(`/client-plans/${id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const runExpiryCheck = async () => {
    setRunning(true);
    try {
      const res = await apiPost("/admin/notifications/run-expiry-check", { threshold_days: 2 });
      toast.success(`Chequeo ejecutado · ${res.candidates_found} candidatos · ${res.sent} email(s) enviado(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error ejecutando chequeo");
    } finally { setRunning(false); }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await apiPost("/admin/notifications/test-email", {});
      toast.success(`Email de prueba enviado a ${res.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando prueba");
    } finally { setTestingEmail(false); }
  };

  const enrich = (cp) => {
    const d = cp.days_left;
    let badge = { label: "Activo", cls: "bg-green-600/20 text-green-300 border-green-600/40" };
    if (cp.status === "cancelled") badge = { label: "Cancelado", cls: "bg-white/10 text-gray-400 border-white/15" };
    else if (typeof d === "number") {
      if (d < 0) badge = { label: `Vencido (${Math.abs(d)}d)`, cls: "bg-red-600/20 text-red-300 border-red-600/40" };
      else if (d <= 2) badge = { label: `Por vencer (${d}d)`, cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" };
      else if (d <= 7) badge = { label: `${d}d restantes`, cls: "bg-blue-500/20 text-blue-300 border-blue-500/40" };
      else badge = { label: `${d}d restantes`, cls: "bg-green-600/20 text-green-300 border-green-600/40" };
    }
    return badge;
  };

  const filtered = items.filter(cp => {
    if (filter === "all") return true;
    if (filter === "expiring") return typeof cp.days_left === "number" && cp.days_left >= 0 && cp.days_left <= 2;
    if (filter === "active") return (cp.status || "active") === "active" && (cp.days_left ?? 0) > 0;
    if (filter === "expired") return typeof cp.days_left === "number" && cp.days_left < 0;
    return true;
  });

  const counts = {
    all: items.length,
    active: items.filter(cp => (cp.status || "active") === "active" && (cp.days_left ?? 0) > 0).length,
    expiring: items.filter(cp => typeof cp.days_left === "number" && cp.days_left >= 0 && cp.days_left <= 2).length,
    expired: items.filter(cp => typeof cp.days_left === "number" && cp.days_left < 0).length,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold font-['Cabinet_Grotesk']">Clientes con Plan Activo</h2>
          <p className="text-xs text-gray-400">{items.length} clientes · Notificación automática 2 días antes de vencer (Email vía Resend)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="border-white/15 text-gray-300" onClick={sendTestEmail} disabled={testingEmail} data-testid="cp-test-email-btn">
            {testingEmail ? "Enviando..." : "Probar email"}
          </Button>
          <Button size="sm" variant="outline" className="border-white/15 text-gray-300" onClick={runExpiryCheck} disabled={running} data-testid="cp-run-check-btn">
            {running ? "Chequeando..." : "Chequear vencimientos"}
          </Button>
          <Button size="sm" className="bg-[#FF4D00] hover:bg-[#FF4D00]/80" onClick={onOpenNew} data-testid="cp-add-btn">
            <Plus size={14} className="mr-1" /> Nuevo cliente
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { v: "all", label: "Todos", c: counts.all },
          { v: "active", label: "Activos", c: counts.active },
          { v: "expiring", label: "Por vencer (≤2d)", c: counts.expiring },
          { v: "expired", label: "Vencidos", c: counts.expired },
        ].map(f => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-3 h-8 text-[11px] uppercase tracking-wider font-bold border ${filter === f.v ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/15 text-gray-300 hover:border-white/40"}`}
            data-testid={`cp-filter-${f.v}`}
          >{f.label} ({f.c})</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500 border border-white/5 bg-[#0F0F0F]">
          <Barbell size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Sin clientes en esta vista.</p>
        </div>
      ) : (
        <div className="border border-white/5" data-testid="client-plans-list">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-gray-400">Cliente</TableHead>
                <TableHead className="text-gray-400">Plan</TableHead>
                <TableHead className="text-gray-400">Inicio</TableHead>
                <TableHead className="text-gray-400">Vence</TableHead>
                <TableHead className="text-gray-400">Estado</TableHead>
                <TableHead className="text-gray-400 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(cp => {
                const badge = enrich(cp);
                return (
                  <TableRow key={cp.id} className="border-white/5">
                    <TableCell>
                      <div className="font-medium">{cp.client_name}</div>
                      <div className="text-xs text-gray-500">{cp.client_email || cp.client_phone || "-"}</div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-300">{cp.plan_title}<div className="text-xs text-gray-500">{cp.duration_weeks} sem.</div></TableCell>
                    <TableCell className="text-sm">{cp.start_date}</TableCell>
                    <TableCell className="text-sm">{cp.end_date}</TableCell>
                    <TableCell>
                      <div className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${badge.cls}`}>{badge.label}</div>
                      {cp.notified_expiring && <div className="text-[10px] text-gray-500 mt-1">📧 Notificado</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="border-white/15 text-gray-300 mr-1" onClick={() => onOpenEdit(cp)} data-testid={`cp-edit-${cp.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => onDelete(cp.id)} data-testid={`cp-delete-${cp.id}`}>
                        <Trash size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente con plan activo"}</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              La fecha de fin se calcula automáticamente según las semanas. Recibirás email de aviso 2 días antes del vencimiento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-400">Nombre del cliente *</Label>
              <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="bg-[#121212] border-white/10" data-testid="cp-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-400">Email</Label>
                <Input value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} className="bg-[#121212] border-white/10" data-testid="cp-form-email" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Teléfono</Label>
                <Input value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} className="bg-[#121212] border-white/10" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-400">Plan asignado *</Label>
              <Select value={form.plan_id} onValueChange={(v) => {
                const p = plans.find(x => x.id === v);
                setForm({ ...form, plan_id: v, duration_weeks: p?.duration_weeks || form.duration_weeks });
              }}>
                <SelectTrigger className="bg-[#121212] border-white/10" data-testid="cp-form-plan">
                  <SelectValue placeholder="Selecciona un plan" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  {plans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title} · {p.duration_weeks} sem.</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-400">Fecha de inicio *</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="bg-[#121212] border-white/10" data-testid="cp-form-start" />
              </div>
              <div>
                <Label className="text-xs text-gray-400">Duración (semanas)</Label>
                <Input type="number" min="1" value={form.duration_weeks} onChange={e => setForm({ ...form, duration_weeks: e.target.value })} className="bg-[#121212] border-white/10" data-testid="cp-form-duration" />
              </div>
            </div>
            {editing && (
              <div>
                <Label className="text-xs text-gray-400">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-[#121212] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                    <SelectItem value="finished">Finalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-gray-400">Notas</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-[#121212] border-white/10" rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="border-white/15 text-gray-300" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#FF4D00] hover:bg-[#FF4D00]/80" onClick={onSave} data-testid="cp-form-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
